/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 3 PRO React components

// Billing page components
import BaseLayout from "layouts/pages/tugas/components/BaseLayout";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import MDDropzone from "components/MDDropzone";
import MDDatePicker from "components/MDDatePicker";
import swal from "sweetalert";
import Autocomplete from "@mui/material/Autocomplete";
import MDInput from "components/MDInput";

import { useMaterialUIController } from "context";

import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD, API_CHAT } from "utils";
import { logoutUser } from "actions/AuthAction";
import DataTable from "examples/Tables/DataTable";
import ChatIcon from "@mui/icons-material/Chat";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";

const PRIORITAS_COLOR = {
  Rendah: "#4caf50",
  Sedang: "#ffb300",
  Tinggi: "#f44336",
};

// Validasi tipe file
const isValidFileType = (file) => {
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "image/jpeg",
    "image/jpg",
  ];
  const allowedExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".xlsm",
    ".jpg",
    ".jpeg",
  ];
  const fileExtension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf("."));
  return (
    allowedTypes.includes(file.type) ||
    allowedExtensions.includes(fileExtension)
  );
};

// Validasi total ukuran semua file (1.5 MB = 1.5 * 1024 * 1024 bytes)
const isValidTotalSize = (files) => {
  const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB total dalam bytes
  const totalSize = files.reduce((sum, file) => {
    const fileObj = file.file || file;
    return sum + (fileObj.size || 0);
  }, 0);
  return totalSize <= maxTotalSize;
};

// Helper untuk memilih icon dokumen
function getFileIcon(filename) {
  const ext = filename.split(".").pop().toLowerCase();
  if (ext === "pdf")
    return (
      <PictureAsPdfIcon sx={{ color: "#f44336", mr: 1 }} fontSize="small" />
    );
  if (["png", "jpg", "jpeg", "gif", "bmp", "svg", "webp"].includes(ext))
    return <ImageIcon sx={{ color: "#1976d2", mr: 1 }} fontSize="small" />;
  if (["doc", "docx"].includes(ext))
    return (
      <DescriptionIcon sx={{ color: "#1565c0", mr: 1 }} fontSize="small" />
    );
  if (["xls", "xlsx"].includes(ext))
    return (
      <DescriptionIcon sx={{ color: "#388e3c", mr: 1 }} fontSize="small" />
    );
  if (["ppt", "pptx"].includes(ext))
    return (
      <DescriptionIcon sx={{ color: "#ff9800", mr: 1 }} fontSize="small" />
    );
  return (
    <InsertDriveFileIcon sx={{ color: "#757575", mr: 1 }} fontSize="small" />
  );
}

// Helper format tanggal
function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// Data chart instruksi dibuat & diselesaikan
const chartInstruksi = {
  labels: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "Mei",
    "Jun",
    "Jul",
    "Agu",
    "Sep",
    "Okt",
    "Nov",
    "Des",
  ],
  datasets: [
    {
      chartType: "thin-bar",
      label: "Instruksi Dibuat",
      color: "info",
      data: [2, 3, 4, 5, 2, 1, 3, 4, 2, 3, 2, 1],
    },
    {
      chartType: "gradient-line",
      label: "Instruksi Selesai",
      color: "success",
      data: [1, 2, 2, 3, 1, 2, 2, 3, 1, 2, 1, 2],
    },
  ],
};

function DaftarPenugasan() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [instruksiTerima, setInstruksiTerima] = useState([]);
  const [tahun, setTahun] = useState(null);
  const [bulan, setBulan] = useState(null);
  const [statistik, setStatistik] = useState({
    total_instruksi: 0,
    total_proses: 0,
    total_selesai: 0,
    total_belum_diterima: 0,
  });
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState(""); // 'lanjutkan' | 'selesaikan'
  const [selectedInstruksi, setSelectedInstruksi] = useState(null);
  const [modalValues, setModalValues] = useState({
    catatan: "",
    files: [],
    penerima: [],
    tingkat_perintah: "",
    status_pelaksana: "",
    waktu_diterima: "",
    waktu_selesai: "",
  });
  const [loadingModal, setLoadingModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [penerimaOptions, setPenerimaOptions] = useState([]);
  const [penerima, setPenerima] = useState({ penerima: [] });
  const dropzoneRef = useRef(null);
  const dropzoneRefTL = useRef(null); // Ref untuk dropzone dialog selesaikan instruksi
  const [openDiteruskan, setOpenDiteruskan] = useState({});
  const [openDiteruskanDialog, setOpenDiteruskanDialog] = useState(false);
  const [selectedDiteruskanData, setSelectedDiteruskanData] = useState(null);
  const [tingkatPerintah, setTingkatPerintah] = useState("");
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openTindakLanjutDialog, setOpenTindakLanjutDialog] = useState(false);
  const [openPilihanDialog, setOpenPilihanDialog] = useState(false);
  const [openInstruksiAsalDialog, setOpenInstruksiAsalDialog] = useState(false);
  const [selectedInstruksiAsal, setSelectedInstruksiAsal] = useState(null);
  const [selectedInstruksiTL, setSelectedInstruksiTL] = useState(null);
  const [keteranganTL, setKeteranganTL] = useState("");
  const [tanggalTL, setTanggalTL] = useState(new Date());
  const [filesTL, setFilesTL] = useState([]);
  const [totalSizeTL, setTotalSizeTL] = useState(0);
  const [showSizeWarningTL, setShowSizeWarningTL] = useState(false);
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false); // State untuk track apakah file melebihi batas di dialog lanjutkan
  const [tindakLanjutType, setTindakLanjutType] = useState(""); // 'selesai' atau 'lanjutkan'
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openDeleteInstruksiDialog, setOpenDeleteInstruksiDialog] =
    useState(false);
  const [deleteInstruksiTarget, setDeleteInstruksiTarget] = useState(null);
  const [statusUser, setStatusUser] = useState(null);
  // State untuk chat
  const [openChatDialog, setOpenChatDialog] = useState(false);
  const [selectedChatInstruksi, setSelectedChatInstruksi] = useState(null);
  const [selectedChatPenerima, setSelectedChatPenerima] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingChat, setLoadingChat] = useState(false);
  const [refreshingChat, setRefreshingChat] = useState(false);
  const chatEndRef = useRef(null);
  // Ambil darkMode dari context
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const navigate = useNavigate();

  useEffect(() => {
    // Ambil dinas_id dari localStorage
    let dinas_id = null;
    let user_id = null;
    let status = null;
    try {
      const user = localStorage.getItem("user");
      if (user) {
        const parsed = JSON.parse(user);
        dinas_id = parsed.dinas_id;
        user_id = parsed.uid;
        status = parsed.status;
      } else {
        logoutUser(navigate);
      }
    } catch (e) {}
    if (!dinas_id) return;
    axios
      .get(
        status === "pimpinan"
          ? `${API_ESA_OFFICE}users/pimpinan/list`
          : `${API_ESA_OFFICE}users/dinas/${dinas_id}`
      )
      .then((res) => {
        const options = (res.data || [])
          .filter((data) => data.user_id !== user_id)
          .map((data) => ({
            label: data.nama ? data.nama : "-",
            value: data.user_id,
            avatar: data.uid_RSS
              ? `https://smartdh.rotendaokab.go.id/uploads/${data.uid_RSS}.jpg`
              : undefined,
          }));
        setPenerimaOptions(options);
      })
      .catch((err) => {
        setPenerimaOptions([]);
      });
    // eslint-disable-next-line
  }, []);

  const allOptions = [
    { label: "Pilih Semua", value: "all", avatar: "" },
    ...penerimaOptions,
  ];

  const handleChange = (event, value) => {
    const isSelectAll = value.some((v) => v.value === "all");
    if (isSelectAll) {
      if ((penerima.penerima || []).length === penerimaOptions.length) {
        setPenerima({ penerima: [] });
        setModalValues((prev) => ({ ...prev, penerima: [] }));
      } else {
        setPenerima({ penerima: penerimaOptions });
        setModalValues((prev) => ({ ...prev, penerima: penerimaOptions }));
      }
    } else {
      setPenerima({ penerima: value });
      setModalValues((prev) => ({ ...prev, penerima: value }));
    }
  };

  useEffect(() => {
    // Ambil userId dari localStorage saat mount
    let uid = null;
    try {
      const user = localStorage.getItem("user");
      if (user) {
        const parsed = JSON.parse(user);
        uid = parsed.uid;
      }
    } catch (e) {}
    setUserId(uid);

    // Set tahun dan bulan saat ini
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = (currentDate.getMonth() + 1).toString(); // Pastikan sebagai string
    setTahun(currentYear);
    setBulan(currentMonth);
  }, []);

  const fetchStatistik = async (tahunParam, bulanParam) => {
    if (!userId || !tahunParam) return;

    try {
      const bulanQuery =
        bulanParam && bulanParam !== "semua" && bulanParam !== "0"
          ? `&bulan=${bulanParam}`
          : "";
      const response = await axios.get(
        `${API_ESA_OFFICE}instruksi/terima/${userId}/statistik?tahun=${tahunParam}${bulanQuery}`
      );
      setStatistik(response.data);
    } catch (error) {
      console.error("Error fetching statistik:", error);
    }
  };

  function ambilData() {
    if (!userId || !tahun) return;

    // Jika bulan belum di-set, set ke bulan saat ini
    if (!bulan) {
      const currentMonth = (new Date().getMonth() + 1).toString(); // Pastikan sebagai string
      setBulan(currentMonth);
      return;
    }

    // Fetch data instruksi diterima oleh user dengan filter
    const bulanParam =
      bulan && bulan !== "semua" && bulan !== "0" ? `&bulan=${bulan}` : "";
    axios
      .get(
        `${API_ESA_OFFICE}instruksi/terima/${userId}?tahun=${tahun}${bulanParam}`
      )
      .then((response) => {
        setInstruksiTerima(response.data);
      })
      .catch((error) => {
        console.error("Error fetching instruksi:", error);
        setInstruksiTerima([]);
      });

    // Fetch statistik dengan parameter yang sama
    fetchStatistik(tahun, bulan);
  }

  // Helper untuk render file button
  const renderFileButton = (files, instruksiId) => {
    if (!files || files.length === 0) {
      return (
        <MDTypography variant="caption" color="text">
          -
        </MDTypography>
      );
    }
    const fileList = Array.isArray(files) ? files : [files];
    return (
      <MDBox display="flex" flexDirection="column" gap={0.5}>
        {fileList.slice(0, 2).map((file, idx) => {
          const filename =
            typeof file === "string"
              ? file.split("/").pop()
              : file.name || `File ${idx + 1}`;
          return (
            <Button
              key={idx}
              href={`${API_ESA_OFFICE_UPLOAD}${file}`}
              target="_blank"
              size="small"
              sx={{
                textTransform: "none",
                justifyContent: "flex-start",
                minWidth: 90,
              }}
              variant="outlined"
              startIcon={getFileIcon(filename)}
            >
              {filename.length > 15
                ? filename.substring(0, 15) + "..."
                : filename}
            </Button>
          );
        })}
        {fileList.length > 2 && (
          <MDTypography variant="caption" color="text">
            +{fileList.length - 2} file lainnya
          </MDTypography>
        )}
      </MDBox>
    );
  };

  // Helper untuk render status dengan tanggal diterima dan tombol terima
  const renderStatus = (item) => {
    const statusPelaksana = item.instruksi?.status_pelaksana || "Belum";
    let color = "info";
    let icon = "hourglass_bottom";
    let label = statusPelaksana;

    if (statusPelaksana.toLowerCase() === "selesai") {
      color = "success";
      icon = "check_circle";
    } else if (statusPelaksana.toLowerCase() === "proses") {
      color = "warning";
      icon = "hourglass_bottom";
    } else if (statusPelaksana.toLowerCase() === "belum") {
      color = "error";
      icon = "cancel";
    }

    // Hitung deadline untuk semua status
    // waktu_selesai adalah target deadline
    let deadlineInfo = null;
    if (item.instruksi?.waktu_selesai) {
      const targetDeadline = new Date(item.instruksi.waktu_selesai);
      const now = new Date();

      if (!isNaN(targetDeadline.getTime()) && !isNaN(now.getTime())) {
        const diffMs = now - targetDeadline; // Waktu sekarang - target deadline
        const diffHours = Math.abs(diffMs / (1000 * 60 * 60));

        if (diffMs > 0) {
          // Lewat deadline (waktu sekarang > target deadline)
          const days = Math.floor(diffHours / 24);
          const hours = Math.floor(diffHours % 24);

          if (statusPelaksana.toLowerCase() === "selesai") {
            // Jika sudah selesai, bandingkan dengan waktu_diselesaikan
            const waktuSelesaiAktual = item.instruksi?.waktu_diselesaikan
              ? new Date(item.instruksi.waktu_diselesaikan)
              : now;
            const diffMsSelesai = waktuSelesaiAktual - targetDeadline;
            const diffHoursSelesai = Math.abs(diffMsSelesai / (1000 * 60 * 60));
            const daysSelesai = Math.floor(diffHoursSelesai / 24);
            const hoursSelesai = Math.floor(diffHoursSelesai % 24);

            if (diffMsSelesai > 0) {
              deadlineInfo = (
                <MDTypography
                  variant="caption"
                  color="error"
                  sx={{ fontSize: "0.7rem", fontWeight: "medium" }}
                >
                  Lewat: {daysSelesai > 0 ? `${daysSelesai} hari ` : ""}
                  {Math.floor(hoursSelesai)} jam
                </MDTypography>
              );
            } else {
              deadlineInfo = (
                <MDTypography
                  variant="caption"
                  color="success"
                  sx={{ fontSize: "0.7rem", fontWeight: "medium" }}
                >
                  Sebelum: {daysSelesai > 0 ? `${daysSelesai} hari ` : ""}
                  {Math.floor(hoursSelesai)} jam
                </MDTypography>
              );
            }
          } else {
            // Status belum atau proses - tampilkan informasi lewat deadline
            deadlineInfo = (
              <MDTypography
                variant="caption"
                color="error"
                sx={{ fontSize: "0.7rem", fontWeight: "medium" }}
              >
                Lewat: {days > 0 ? `${days} hari ` : ""}
                {Math.floor(hours)} jam
              </MDTypography>
            );
          }
        } else if (statusPelaksana.toLowerCase() === "selesai") {
          // Sebelum deadline untuk status selesai
          const waktuSelesaiAktual = item.instruksi?.waktu_diselesaikan
            ? new Date(item.instruksi.waktu_diselesaikan)
            : now;
          const diffMsSelesai = waktuSelesaiAktual - targetDeadline;
          const diffHoursSelesai = Math.abs(diffMsSelesai / (1000 * 60 * 60));
          const daysSelesai = Math.floor(diffHoursSelesai / 24);
          const hoursSelesai = Math.floor(diffHoursSelesai % 24);

          deadlineInfo = (
            <MDTypography
              variant="caption"
              color="success"
              sx={{ fontSize: "0.7rem", fontWeight: "medium" }}
            >
              Sebelum: {daysSelesai > 0 ? `${daysSelesai} hari ` : ""}
              {Math.floor(hoursSelesai)} jam
            </MDTypography>
          );
        }
      }
    }

    return (
      <MDBox display="flex" flexDirection="column" gap={0.5}>
        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Icon sx={{ color: `${color}.main` }}>{icon}</Icon>
          <MDTypography variant="caption" color={color}>
            {label}
          </MDTypography>
        </MDBox>
        {item.instruksi?.waktu_diterima && (
          <MDTypography
            variant="caption"
            color="text"
            sx={{ fontSize: "0.7rem" }}
          >
            Diterima: {formatDate(item.instruksi.waktu_diterima)}
          </MDTypography>
        )}
        {!item.instruksi?.waktu_diterima &&
          statusPelaksana.toLowerCase() === "belum" && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ mt: 0.5, textTransform: "none" }}
              onClick={() => {
                if (item.instruksi?.instruksi_penerima_id) {
                  handleTerima(item.instruksi.instruksi_penerima_id);
                }
              }}
            >
              Terima
            </Button>
          )}
        {deadlineInfo}
      </MDBox>
    );
  };

  // Helper untuk handle klik file (PDF = dialog, lainnya = download)
  const handleFileClick = (fileUrl, fileName) => {
    if (!fileUrl) return;

    const fileExtension = fileName?.split(".").pop()?.toLowerCase() || "";
    const isPdf = fileExtension === "pdf";

    if (isPdf) {
      // Buka dialog untuk PDF
      const fullUrl = fileUrl.startsWith("http")
        ? fileUrl
        : `${API_ESA_OFFICE_UPLOAD}${fileUrl}`;
      setSelectedFile(fullUrl);
      setOpenFileDialog(true);
    } else {
      // Download langsung untuk non-PDF
      const fullUrl = fileUrl.startsWith("http")
        ? fileUrl
        : `${API_ESA_OFFICE_UPLOAD}${fileUrl}`;
      const link = document.createElement("a");
      link.href = fullUrl;
      link.download = fileName || "file";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Helper untuk render file dengan keterangan
  const renderFile = (row) => {
    const item = row.original;
    // Dokumen (Dok Instruksi) - dari instruksi_asal
    let dokumenArr = [];
    if (item.instruksi_asal?.dokumen) {
      if (Array.isArray(item.instruksi_asal.dokumen)) {
        dokumenArr = item.instruksi_asal.dokumen;
      } else if (
        typeof item.instruksi_asal.dokumen === "string" &&
        item.instruksi_asal.dokumen.trim().startsWith("[")
      ) {
        try {
          dokumenArr = JSON.parse(item.instruksi_asal.dokumen);
        } catch (e) {
          dokumenArr = [];
        }
      }
    }

    // File (Dok Tindak Lanjut) - dari instruksi
    let fileArr = [];
    if (item.instruksi?.file_tindak_lanjut) {
      if (Array.isArray(item.instruksi.file_tindak_lanjut)) {
        fileArr = item.instruksi.file_tindak_lanjut;
      } else if (
        typeof item.instruksi.file_tindak_lanjut === "string" &&
        item.instruksi.file_tindak_lanjut.trim().startsWith("[")
      ) {
        try {
          fileArr = JSON.parse(item.instruksi.file_tindak_lanjut);
        } catch (e) {
          fileArr = [];
        }
      } else if (
        typeof item.instruksi.file_tindak_lanjut === "string" &&
        item.instruksi.file_tindak_lanjut.length > 0
      ) {
        fileArr = [item.instruksi.file_tindak_lanjut];
      }
    }

    const totalFiles = dokumenArr.length + fileArr.length;

    if (totalFiles === 0) {
      return (
        <MDTypography variant="caption" color="text">
          -
        </MDTypography>
      );
    }

    return (
      <MDBox display="flex" flexDirection="column" gap={0.5}>
        {dokumenArr.length > 0 && (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <DescriptionIcon sx={{ fontSize: 16, color: "#1565c0" }} />
            <MDTypography variant="caption" color="text">
              Dok Instruksi: {dokumenArr.length}
            </MDTypography>
            {dokumenArr.map((file, idx) => {
              const fileName =
                typeof file === "string"
                  ? file.split("/").pop()
                  : `Dok ${idx + 1}`;
              const fileExt = fileName?.split(".").pop()?.toLowerCase() || "";
              const isPdf = fileExt === "pdf";
              return (
                <IconButton
                  key={idx}
                  size="small"
                  onClick={() => handleFileClick(file, fileName)}
                  sx={{ p: 0.5 }}
                  title={fileName}
                >
                  {isPdf ? (
                    <PictureAsPdfIcon sx={{ fontSize: 14, color: "#f44336" }} />
                  ) : (
                    getFileIcon(fileName)
                  )}
                </IconButton>
              );
            })}
          </MDBox>
        )}
        {fileArr.length > 0 && (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <InsertDriveFileIcon sx={{ fontSize: 16, color: "#757575" }} />
            <MDTypography variant="caption" color="text">
              Dok Tindak Lanjut: {fileArr.length}
            </MDTypography>
            {fileArr.map((file, idx) => {
              const fileName =
                typeof file === "string"
                  ? file.split("/").pop()
                  : `File ${idx + 1}`;
              const fileExt = fileName?.split(".").pop()?.toLowerCase() || "";
              const isPdf = fileExt === "pdf";
              return (
                <IconButton
                  key={idx}
                  size="small"
                  onClick={() => handleFileClick(file, fileName)}
                  sx={{ p: 0.5 }}
                  title={fileName}
                >
                  {isPdf ? (
                    <PictureAsPdfIcon sx={{ fontSize: 14, color: "#f44336" }} />
                  ) : (
                    getFileIcon(fileName)
                  )}
                </IconButton>
              );
            })}
          </MDBox>
        )}
      </MDBox>
    );
  };

  // Helper untuk render tombol dilanjutkan
  const renderDilanjutkan = (item) => {
    const diteruskanKe = Array.isArray(item.diteruskan_ke)
      ? item.diteruskan_ke
      : [];
    const count = diteruskanKe.length;

    if (count === 0) {
      return (
        <MDTypography variant="caption" color="text">
          -
        </MDTypography>
      );
    }

    return (
      <Button
        variant="outlined"
        size="small"
        onClick={() => {
          setSelectedDiteruskanData(item);
          setOpenDiteruskanDialog(true);
        }}
        sx={{
          textTransform: "none",
          color: "#000000",
          borderColor: "#000000",
          "&:hover": {
            borderColor: "#000000",
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        Detail ({count})
      </Button>
    );
  };

  // Helper untuk render tombol tindak lanjut
  const renderTindakLanjut = (item) => {
    // Hide jika status sudah selesai
    const statusPelaksana = item.instruksi?.status_pelaksana || "";
    if (statusPelaksana.toLowerCase() === "selesai") {
      return (
        <MDTypography variant="caption" color="text">
          -
        </MDTypography>
      );
    }

    return (
      <Button
        variant="contained"
        color="info"
        size="small"
        onClick={() => {
          setSelectedInstruksiTL(item);
          setOpenPilihanDialog(true);
        }}
      >
        Tindak Lanjut
      </Button>
    );
  };

  // Handler untuk pilih tindak lanjut
  const handlePilihTindakLanjut = (type) => {
    setTindakLanjutType(type);
    setOpenPilihanDialog(false);

    if (type === "selesai") {
      // Buka dialog selesaikan
      setOpenTindakLanjutDialog(true);
      setKeteranganTL("");
      setTanggalTL(new Date());
      setFilesTL([]);
      setTotalSizeTL(0);
      setShowSizeWarningTL(false);
    } else if (type === "lanjutkan") {
      // Buka dialog lanjutkan (modal yang sudah ada)
      setSelectedInstruksi(selectedInstruksiTL);
      setModalType("lanjutkan");
      setOpenModal(true);
      setTingkatPerintah(selectedInstruksiTL.tingkat);
    }
  };

  const handleClosePilihanDialog = () => {
    setOpenPilihanDialog(false);
    setSelectedInstruksiTL(null);
  };

  // Handler untuk dialog file PDF
  const handleCloseFileDialog = () => {
    setOpenFileDialog(false);
    setSelectedFile(null);
  };

  // Handler untuk dialog tindak lanjut
  const handleCloseTindakLanjut = () => {
    setOpenTindakLanjutDialog(false);
    setSelectedInstruksiTL(null);
    setKeteranganTL("");
    setTanggalTL(new Date());
    setFilesTL([]);
    setTotalSizeTL(0);
    setShowSizeWarningTL(false);
  };

  // Event handler untuk memvalidasi dan menolak file sebelum ditambahkan ke dropzone TL
  // Mengikuti pola dari Media/index.js
  useEffect(() => {
    const dz = dropzoneRefTL.current?.dropzone;
    if (!dz) return;

    // Handler untuk memvalidasi file sebelum ditambahkan (menggunakan accept)
    const handleAccept = (file, done) => {
      // Validasi tipe file
      if (!isValidFileType(file)) {
        done("Format file tidak didukung");
        return;
      }

      // Hitung total ukuran file yang sudah ada di dropzone
      const existingFilesSize = dz.files.reduce((sum, f) => {
        return sum + (f.size || 0);
      }, 0);

      // Hitung total ukuran setelah menambahkan file baru
      const totalSize = existingFilesSize + file.size;
      const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB
      const totalSizeMB = totalSize / (1024 * 1024);

      // Jika total ukuran melebihi batas, tolak file
      if (totalSize > maxTotalSize) {
        done(`Total ukuran melebihi 1.5 MB (${totalSizeMB.toFixed(2)} MB)`);

        // Tampilkan pesan error
        swal({
          title: "Ukuran File Melebihi Batas",
          text: `Total ukuran semua file (${totalSizeMB.toFixed(
            2
          )} MB) melebihi batas maksimal 1.5 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
          icon: "error",
          button: "OK",
        });
        return;
      }

      // File diterima
      done(null);
    };

    // Handler untuk memvalidasi multiple files (menggunakan addedfiles sebagai fallback)
    const handleAddedFiles = (files) => {
      // Gunakan setTimeout untuk memastikan file sudah ditambahkan ke dz.files
      setTimeout(() => {
        // Hitung total ukuran file yang sekarang ada di dropzone
        const allFiles = dz.files;
        const totalSize = allFiles.reduce((sum, file) => {
          return sum + (file.size || 0);
        }, 0);
        const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB
        const totalSizeMB = totalSize / (1024 * 1024);

        // Jika total ukuran melebihi batas, hapus SEMUA file yang baru ditambahkan
        if (totalSize > maxTotalSize) {
          // Hapus semua file yang baru ditambahkan (file yang ada di parameter files)
          files.forEach((file) => {
            // Validasi tipe file terlebih dahulu
            if (!isValidFileType(file)) {
              // File dengan tipe tidak valid sudah ditolak oleh dropzone sendiri
              return;
            }
            // Hapus file yang menyebabkan total melebihi batas
            if (dz.files.includes(file)) {
              dz.emit("error", file, `Total ukuran melebihi 1.5 MB`);
              dz.removeFile(file);
            }
          });

          // Update state untuk disable tombol kirim
          setShowSizeWarningTL(true);

          // Tampilkan pesan error
          swal({
            title: "Ukuran File Melebihi Batas",
            text: `Total ukuran semua file (${totalSizeMB.toFixed(
              2
            )} MB) melebihi batas maksimal 1.5 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
            icon: "error",
            button: "OK",
          });
        } else {
          // Update state jika tidak melebihi batas
          setShowSizeWarningTL(false);
        }
      }, 0);
    };

    // Handler untuk update state saat file dihapus
    const handleRemovedFileTL = () => {
      setTimeout(() => {
        const allFiles = dz.files;
        const totalSize = allFiles.reduce((sum, file) => {
          return sum + (file.size || 0);
        }, 0);
        const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB
        setShowSizeWarningTL(totalSize > maxTotalSize);
      }, 0);
    };

    // Tambahkan event listener untuk accept (validasi sebelum file ditambahkan)
    dz.on("accept", handleAccept);

    // Tambahkan event listener untuk addedfiles sebagai fallback
    dz.on("addedfiles", handleAddedFiles);

    // Tambahkan event listener untuk removedfile untuk update state
    dz.on("removedfile", handleRemovedFileTL);

    // Cleanup: hapus event listener saat unmount
    return () => {
      if (dz) {
        dz.off("accept", handleAccept);
        dz.off("addedfiles", handleAddedFiles);
        dz.off("removedfile", handleRemovedFileTL);
      }
    };
  }, [openTindakLanjutDialog]);

  const handleDropzoneChangeTL = (files) => {
    // Mengikuti pola dari Media/index.js: simpan file langsung dari dropzone
    const totalSizeInMB =
      files.reduce((acc, file) => {
        const fileObj = file.file || file;
        return acc + (fileObj.size || 0);
      }, 0) /
      (1024 * 1024);
    setTotalSizeTL(totalSizeInMB);
    const isExceeding = totalSizeInMB > 1.5;
    setShowSizeWarningTL(isExceeding);
    // Jika melebihi 1.5 MB, jangan set files dan kosongkan
    if (isExceeding) {
      setFilesTL([]);
      // Hapus semua file dari dropzone jika melebihi batas
      if (dropzoneRefTL.current?.dropzone) {
        const dz = dropzoneRefTL.current.dropzone;
        dz.files.forEach((file) => {
          dz.removeFile(file);
        });
      }
    } else {
      // Simpan file langsung dari dropzone (mengikuti pola Media/index.js)
      setFilesTL(files);
    }
  };

  const handleSubmitTindakLanjut = async () => {
    // Debug: log data untuk troubleshooting
    console.log(
      "handleSubmitTindakLanjut - selectedInstruksiTL:",
      selectedInstruksiTL
    );
    console.log("handleSubmitTindakLanjut - userId:", userId);

    if (!selectedInstruksiTL || !keteranganTL) {
      swal({
        icon: "warning",
        title: "Peringatan",
        text: "Mohon lengkapi semua data yang diperlukan",
        button: "OK",
      });
      return;
    }

    // Validasi instruksi_penerima_id
    // Struktur data dari backend: { instruksi: { instruksi_penerima_id: ... }, ... }
    const instruksiPenerimaId =
      selectedInstruksiTL?.instruksi?.instruksi_penerima_id;

    console.log(
      "handleSubmitTindakLanjut - instruksiPenerimaId:",
      instruksiPenerimaId
    );

    if (!instruksiPenerimaId) {
      console.error("❌ instruksi_penerima_id tidak ditemukan");
      console.error("selectedInstruksiTL:", selectedInstruksiTL);
      console.error(
        "selectedInstruksiTL.instruksi:",
        selectedInstruksiTL?.instruksi
      );
      swal({
        icon: "error",
        title: "Error",
        text: "Data instruksi tidak valid. Silakan tutup dialog, refresh halaman, dan coba lagi.",
        button: "OK",
      });
      return;
    }

    if (!userId) {
      console.error("❌ userId tidak ditemukan");
      swal({
        icon: "error",
        title: "Error",
        text: "User ID tidak ditemukan. Silakan login ulang.",
        button: "OK",
      });
      return;
    }

    console.log("✅ Semua validasi passed, akan submit dengan:", {
      instruksiPenerimaId,
      userId,
      url: `${API_ESA_OFFICE}instruksi/selesaikan/${instruksiPenerimaId}/${userId}`,
    });

    // Validasi total ukuran file jika ada file (mengikuti pola dari Media/index.js)
    if (filesTL.length > 0) {
      const actualTotalSize = filesTL.reduce((sum, file) => {
        const fileObj = file.file || file;
        return sum + (fileObj.size || 0);
      }, 0);
      const actualTotalSizeMB = actualTotalSize / (1024 * 1024);

      if (actualTotalSizeMB > 1.5) {
        swal({
          icon: "error",
          title: "Ukuran File Melebihi Batas",
          text: `Total ukuran semua file (${actualTotalSizeMB.toFixed(
            2
          )} MB) melebihi batas maksimal 1.5 MB. File tidak dapat disimpan.`,
          button: "OK",
        });
        return;
      }
    }

    try {
      setLoadingModal(true);

      const formData = new FormData();
      formData.append("catatan_selesai", keteranganTL);
      formData.append("waktu_selesai", tanggalTL.toISOString().split("T")[0]);

      // Tambahkan file jika ada (mengikuti pola dari tugas-baru)
      filesTL.forEach((dzFile) => {
        // Pastikan file yang dikirim adalah File asli (mengikuti pola tugas-baru)
        formData.append("file_tindak_lanjut", dzFile.file || dzFile);
      });

      await axios.put(
        `${API_ESA_OFFICE}instruksi/selesaikan/${instruksiPenerimaId}/${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      swal({
        icon: "success",
        title: "Berhasil",
        text: "Tindak lanjut berhasil disimpan",
        button: "OK",
      });

      handleCloseTindakLanjut();
      ambilData();
    } catch (error) {
      console.error("Error submitting tindak lanjut:", error);
      swal({
        icon: "error",
        title: "Gagal",
        text: error.response?.data?.message || "Gagal menyimpan tindak lanjut",
        button: "OK",
      });
    } finally {
      setLoadingModal(false);
    }
  };

  useEffect(() => {
    if (!userId || !tahun || !bulan) return;
    ambilData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tahun, bulan]);

  const handleTerima = async (instruksiPenerimaId) => {
    try {
      await axios.put(
        `${API_ESA_OFFICE}instruksi/terima/${instruksiPenerimaId}`
      );
      swal({
        icon: "success",
        title: "Berhasil",
        text: "Instruksi berhasil diterima",
        button: "OK",
      });
      ambilData();
    } catch (error) {
      console.error("Error accepting instruksi:", error);
      swal({
        icon: "error",
        title: "Gagal",
        text: error.response?.data?.error || "Gagal menerima instruksi",
        button: "OK",
      });
    }
  };

  // Fungsi untuk mendapatkan fresh token
  const getFreshToken = async (user) => {
    if (!user?.uid_RSS) {
      throw new Error("User uid_RSS tidak ditemukan");
    }

    try {
      const tokenResponse = await axios.get(
        `${API_ESA_OFFICE}users/uid/${user.uid_RSS}`,
        {
          withCredentials: true, // Kirim cookie untuk refresh token
        }
      );

      if (tokenResponse.data?.accessToken) {
        const token = tokenResponse.data.accessToken;
        // Simpan token ke localStorage
        const updatedUser = { ...user, accessToken: token };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        return token;
      }
      throw new Error("Access token tidak ditemukan di response");
    } catch (error) {
      console.error("Error getting fresh token:", error);

      // Handle error khusus untuk status_EO tidak aktif
      if (error.response && error.response.status === 403) {
        const errorData = error.response.data;
        const errorMessage =
          errorData.reason || errorData.message || "Akun Anda tidak aktif.";

        // Clear localStorage dan logout user
        localStorage.removeItem("user");
        localStorage.removeItem("daftarHadir");

        swal({
          title: "Akses Ditolak",
          text: errorMessage,
          icon: "error",
          button: "OK",
        }).then(() => {
          window.location.href = "/authentication/sign-in/cover";
        });

        throw new Error(errorMessage);
      }

      throw error;
    }
  };

  // Fungsi untuk fetch chat history
  const fetchChatHistory = async (instruksiItem, token) => {
    const instruksiId =
      instruksiItem.instruksi_asal?.instruksi_id || instruksiItem.instruksi_id;

    // Fetch chat history
    let response;
    try {
      response = await axios.get(`${API_CHAT}instruksi/${instruksiId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });
    } catch (error) {
      // Jika 403, coba refresh token sekali lagi
      if (error.response?.status === 403) {
        console.log("🔄 Token mungkin expired, refreshing...");
        try {
          const user = JSON.parse(localStorage.getItem("user")) || {};
          token = await getFreshToken(user);
          response = await axios.get(`${API_CHAT}instruksi/${instruksiId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          });
        } catch (retryError) {
          console.error(
            "❌ Chat API Error after retry:",
            retryError.response?.data || retryError.message
          );
          throw retryError;
        }
      } else {
        throw error;
      }
    }

    // Set chat messages
    if (response && response.data && response.data.success) {
      const messages = response.data.data || [];
      setChatMessages(messages);

      // Auto mark all messages as read
      try {
        const user = JSON.parse(localStorage.getItem("user")) || {};
        const currentUserId =
          userId || user.uid_RSS || user.user_id || user.uid;
        const markReadResponse = await axios.put(
          `${API_CHAT}instruksi/${instruksiId}/read-all`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
        if (markReadResponse.data.success) {
          console.log("✅ All messages marked as read");
          setChatMessages((prev) =>
            prev.map((msg) => {
              if (!msg.waktu_baca && msg.penerima_id === currentUserId) {
                return { ...msg, waktu_baca: new Date().toISOString() };
              }
              return msg;
            })
          );
        }
      } catch (markReadError) {
        console.warn("⚠️ Failed to mark messages as read:", markReadError);
      }
    }

    return response;
  };

  // Handler untuk refresh chat messages
  const handleRefreshChat = async () => {
    if (!selectedChatInstruksi || refreshingChat) {
      return;
    }

    setRefreshingChat(true);
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setRefreshingChat(false);
        return;
      }

      const token = await getFreshToken(user);
      await fetchChatHistory(selectedChatInstruksi, token);

      // Scroll to bottom after refresh
      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
    } catch (error) {
      console.error("Error refreshing chat:", error);
      swal("Error", "Gagal refresh chat. Silakan coba lagi.", "error");
    } finally {
      setRefreshingChat(false);
    }
  };

  // Handler untuk membuka chat
  const handleOpenChat = async (instruksiItem, penerima) => {
    setSelectedChatInstruksi(instruksiItem);
    setSelectedChatPenerima(penerima);
    setOpenChatDialog(true);
    setLoadingChat(true);
    setChatMessages([]);
    setNewMessage(""); // Reset input message

    try {
      // Ambil user dari localStorage
      const user = JSON.parse(localStorage.getItem("user")) || {};

      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setLoadingChat(false);
        return;
      }

      // Selalu dapatkan fresh token untuk memastikan tidak expired
      let token;
      try {
        token = await getFreshToken(user);
        console.log("✅ Fresh token obtained");
      } catch (tokenError) {
        console.error("Error getting fresh token:", tokenError);
        swal(
          "Error",
          "Gagal mendapatkan token akses. Silakan login ulang.",
          "error"
        );
        setLoadingChat(false);
        return;
      }

      if (!token) {
        swal("Error", "Token tidak ditemukan. Silakan login ulang.", "error");
        setLoadingChat(false);
        return;
      }

      // Fetch chat history
      try {
        await fetchChatHistory(instruksiItem, token);
        // Scroll to bottom
        setTimeout(() => {
          if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      } catch (error) {
        console.error("Error loading chat:", error);
        if (error.response?.status >= 400) {
          swal(
            "Error",
            error.response?.data?.error || "Gagal memuat chat",
            "error"
          );
        }
        setChatMessages([]);
      }
    } catch (error) {
      console.error("Error loading chat:", error);
      swal("Error", "Gagal memuat chat. Silakan coba lagi.", "error");
    } finally {
      setLoadingChat(false);
    }
  };

  // Handler untuk menutup chat
  const handleCloseChat = () => {
    setOpenChatDialog(false);
    setSelectedChatInstruksi(null);
    setSelectedChatPenerima(null);
    setChatMessages([]);
    setNewMessage("");
  };

  // Handler untuk mengirim pesan
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChatInstruksi) {
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage(""); // Clear input immediately

    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};

      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setNewMessage(messageText); // Restore message on error
        return;
      }

      // Dapatkan fresh token
      let token;
      try {
        token = await getFreshToken(user);
      } catch (tokenError) {
        console.error("Error getting fresh token:", tokenError);
        swal(
          "Error",
          "Gagal mendapatkan token akses. Silakan login ulang.",
          "error"
        );
        setNewMessage(messageText); // Restore message on error
        return;
      }

      if (!token) {
        swal("Error", "Token tidak ditemukan. Silakan login ulang.", "error");
        setNewMessage(messageText); // Restore message on error
        return;
      }

      // Jangan kirim instruksi_penerima_id agar semua pesan masuk ke room yang sama
      // Room name akan menjadi instruksi_${instruksi_id} tanpa instruksi_penerima_id
      // Pastikan instruksi_id konsisten (number)
      const instruksiIdRaw =
        selectedChatInstruksi.instruksi_asal?.instruksi_id ||
        selectedChatInstruksi.instruksi_id;
      const instruksiId = parseInt(instruksiIdRaw);
      const roomName = `instruksi_${instruksiId}`;

      // penerima_id adalah id dari pembuat instruksi (yang diajak chat)
      // selectedChatPenerima adalah pembuat instruksi (asal_perintah_dari)
      const penerimaId =
        selectedChatPenerima?.user_id ||
        selectedChatPenerima?.uid ||
        selectedChatPenerima?.penerima_id ||
        selectedChatInstruksi?.instruksi_asal?.pengirim?.user_id ||
        selectedChatInstruksi?.instruksi_asal?.pengirim?.uid ||
        selectedChatInstruksi?.instruksi_asal?.pengirim_id;

      console.log("📤 Sending message from daftar-penugasan:", {
        instruksi_id_raw: instruksiIdRaw,
        instruksi_id: instruksiId,
        room_name: roomName,
        instruksi_asal_id: selectedChatInstruksi.instruksi_asal?.instruksi_id,
        instruksi_id_direct: selectedChatInstruksi.instruksi_id,
        penerima_id: penerimaId,
        selectedChatPenerima: selectedChatPenerima,
      });

      const messageData = {
        instruksi_id: instruksiId, // Pastikan sebagai number
        // Tidak kirim instruksi_penerima_id agar semua user di instruksi yang sama menerima pesan
        instruksi_penerima_id: null,
        penerima_id: penerimaId,
        pesan: messageText,
      };

      // Optimistic update - tambahkan pesan langsung ke state
      const currentUser = JSON.parse(localStorage.getItem("user")) || {};
      const currentUserId = userId || currentUser.uid || currentUser.user_id;
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const tempMessage = {
        id_chat: tempId,
        pesan: messageText,
        pengirim_id: currentUserId,
        pengirim_nama: currentUser.nama || user.nama || "Anda",
        pengirim_profil: currentUser.uid_RSS || user.uid_RSS,
        waktu_kirim: new Date().toISOString(),
        isSending: true, // Flag untuk menandai pesan sedang dikirim
      };

      // Tambahkan pesan langsung ke state (optimistic update)
      setChatMessages((prev) => {
        // Pastikan tidak ada duplikasi
        const exists = prev.some((msg) => msg.id_chat === tempId);
        if (exists) return prev;
        return [...prev, tempMessage];
      });

      // Scroll ke bawah setelah menambahkan pesan
      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);

      // Kirim via REST API
      let response;
      try {
        response = await axios.post(`${API_CHAT}instruksi`, messageData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        });

        if (response.data && response.data.success) {
          // Hapus pesan temporary
          setChatMessages((prev) =>
            prev.filter((msg) => msg.id_chat !== tempId)
          );

          // Refresh chat untuk mendapatkan pesan terbaru
          await fetchChatHistory(selectedChatInstruksi, token);

          // Scroll to bottom
          setTimeout(() => {
            if (chatEndRef.current) {
              chatEndRef.current.scrollIntoView({ behavior: "smooth" });
            }
          }, 150);
        }
      } catch (error) {
        console.error("Error sending message:", error);
        // Hapus pesan temporary jika error
        setChatMessages((prev) => prev.filter((msg) => msg.id_chat !== tempId));
        setNewMessage(messageText); // Restore message
        swal(
          "Error",
          error.response?.data?.error || "Gagal mengirim pesan",
          "error"
        );
        return;
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // Hapus pesan temporary jika error
      setChatMessages((prev) =>
        prev.filter((msg) => !msg.isSending || msg.pesan !== messageText)
      );
      setNewMessage(messageText); // Restore message
      swal(
        "Error",
        error.response?.data?.error || "Gagal mengirim pesan",
        "error"
      );
    }
  };

  // Handler untuk delete message
  const handleDeleteMessage = async (chatId) => {
    if (!chatId) return;

    const confirmed = await swal({
      title: "Hapus Pesan?",
      text: "Pesan yang dihapus tidak dapat dikembalikan.",
      icon: "warning",
      buttons: {
        cancel: {
          text: "Batal",
          value: false,
          visible: true,
        },
        confirm: {
          text: "Hapus",
          value: true,
          visible: true,
        },
      },
      dangerMode: true,
    });

    if (!confirmed) return;

    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const token = await getFreshToken(user);

      await axios.delete(`${API_CHAT}instruksi/${chatId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      // Hapus pesan dari state
      setChatMessages((prev) => prev.filter((msg) => msg.id_chat !== chatId));
      swal("Berhasil", "Pesan berhasil dihapus", "success");
    } catch (error) {
      console.error("Error deleting message:", error);
      swal("Error", "Gagal menghapus pesan", "error");
    }
  };

  // Format waktu untuk chat
  const formatChatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Format waktu: HH:mm
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const timeStr = `${hours}:${minutes}`;

    // Jika hari ini
    if (diffDays === 0) {
      return timeStr;
    }

    // Jika kemarin
    if (diffDays === 1) {
      return `Kemarin ${timeStr}`;
    }

    // Jika dalam seminggu
    if (diffDays < 7) {
      const dayNames = [
        "Minggu",
        "Senin",
        "Selasa",
        "Rabu",
        "Kamis",
        "Jumat",
        "Sabtu",
      ];
      return `${dayNames[date.getDay()]} ${timeStr}`;
    }

    // Jika lebih dari seminggu
    const day = date.getDate();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "Mei",
      "Jun",
      "Jul",
      "Agu",
      "Sep",
      "Okt",
      "Nov",
      "Des",
    ];
    const month = monthNames[date.getMonth()];

    // Jika tahun berbeda, tambahkan tahun
    if (date.getFullYear() !== now.getFullYear()) {
      return `${day} ${month} ${date.getFullYear()} ${timeStr}`;
    }

    return `${day} ${month} ${timeStr}`;
  };

  // Auto scroll ke bawah saat ada pesan baru
  useEffect(() => {
    if (chatEndRef.current && chatMessages.length > 0) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  // Handler modal input
  const handleModalInput = (field, value) => {
    setModalValues((prev) => ({ ...prev, [field]: value }));
  };

  // Inject file ke Dropzone saat modalValues.files berubah (hanya jika modal terbuka)
  // Mengikuti pola dari Media/index.js
  useEffect(() => {
    if (
      dropzoneRef.current &&
      modalValues.files &&
      Array.isArray(modalValues.files) &&
      modalValues.files.length > 0 &&
      openModal
    ) {
      const dz = dropzoneRef.current.dropzone;
      // Hanya inject jika Dropzone kosong
      if (dz && dz.files.length === 0) {
        modalValues.files.forEach((file) => {
          const fileObj = file.file || file;
          // Validasi file sebelum inject
          if (!isValidFileType(fileObj)) {
            console.warn(`File ${fileObj.name} tidak valid dan akan diabaikan`);
            return;
          }
          // Hindari duplikasi file
          if (
            !dz.files.find(
              (f) => f.name === fileObj.name && f.size === fileObj.size
            )
          ) {
            dz.emit("addedfile", fileObj);
            dz.emit("complete", fileObj);
          }
        });
      }
    }
  }, [modalValues.files, openModal]);

  // Event handler untuk memvalidasi dan menolak file sebelum ditambahkan ke dropzone
  useEffect(() => {
    const dz = dropzoneRef.current?.dropzone;
    if (!dz) return;

    // Handler untuk memvalidasi file sebelum ditambahkan (menggunakan accept)
    const handleAccept = (file, done) => {
      // Validasi tipe file
      if (!isValidFileType(file)) {
        done("Format file tidak didukung");
        return;
      }

      // Hitung total ukuran file yang sudah ada di dropzone
      const existingFilesSize = dz.files.reduce((sum, f) => {
        return sum + (f.size || 0);
      }, 0);

      // Hitung total ukuran setelah menambahkan file baru
      const totalSize = existingFilesSize + file.size;
      const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB
      const totalSizeMB = totalSize / (1024 * 1024);

      // Jika total ukuran melebihi batas, tolak file
      if (totalSize > maxTotalSize) {
        done(`Total ukuran melebihi 1.5 MB (${totalSizeMB.toFixed(2)} MB)`);

        // Tampilkan pesan error
        swal({
          title: "Ukuran File Melebihi Batas",
          text: `Total ukuran semua file (${totalSizeMB.toFixed(
            2
          )} MB) melebihi batas maksimal 1.5 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
          icon: "error",
          button: "OK",
        });
        return;
      }

      // File diterima
      done(null);
    };

    // Handler untuk memvalidasi multiple files (menggunakan addedfiles sebagai fallback)
    const handleAddedFiles = (files) => {
      // Gunakan setTimeout untuk memastikan file sudah ditambahkan ke dz.files
      setTimeout(() => {
        // Hitung total ukuran file yang sekarang ada di dropzone
        const allFiles = dz.files;
        const totalSize = allFiles.reduce((sum, file) => {
          return sum + (file.size || 0);
        }, 0);
        const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB
        const totalSizeMB = totalSize / (1024 * 1024);

        // Jika total ukuran melebihi batas, hapus SEMUA file yang baru ditambahkan
        if (totalSize > maxTotalSize) {
          // Hapus semua file yang baru ditambahkan (file yang ada di parameter files)
          files.forEach((file) => {
            // Validasi tipe file terlebih dahulu
            if (!isValidFileType(file)) {
              // File dengan tipe tidak valid sudah ditolak oleh dropzone sendiri
              return;
            }
            // Hapus file yang menyebabkan total melebihi batas
            if (dz.files.includes(file)) {
              dz.emit("error", file, `Total ukuran melebihi 1.5 MB`);
              dz.removeFile(file);
            }
          });

          // Update state untuk disable tombol kirim
          setFileSizeExceeded(true);

          // Tampilkan pesan error
          swal({
            title: "Ukuran File Melebihi Batas",
            text: `Total ukuran semua file (${totalSizeMB.toFixed(
              2
            )} MB) melebihi batas maksimal 1.5 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
            icon: "error",
            button: "OK",
          });
        } else {
          // Update state jika tidak melebihi batas
          setFileSizeExceeded(false);
        }
      }, 0);
    };

    // Handler untuk update state saat file dihapus
    const handleRemovedFile = () => {
      setTimeout(() => {
        const allFiles = dz.files;
        const totalSize = allFiles.reduce((sum, file) => {
          return sum + (file.size || 0);
        }, 0);
        const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB
        setFileSizeExceeded(totalSize > maxTotalSize);
      }, 0);
    };

    // Tambahkan event listener untuk accept (validasi sebelum file ditambahkan)
    dz.on("accept", handleAccept);

    // Tambahkan event listener untuk addedfiles sebagai fallback
    dz.on("addedfiles", handleAddedFiles);

    // Tambahkan event listener untuk removedfile untuk update state
    dz.on("removedfile", handleRemovedFile);

    // Cleanup: hapus event listener saat unmount
    return () => {
      if (dz) {
        dz.off("accept", handleAccept);
        dz.off("addedfiles", handleAddedFiles);
        dz.off("removedfile", handleRemovedFile);
      }
    };
  }, [openModal]);

  // Handler Dropzone (mengikuti pola dari Media/index.js)
  const handleDropzoneChange = (files) => {
    // Filter file yang valid berdasarkan tipe (sebagai double check)
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      const fileObj = file.file || file;
      if (!isValidFileType(fileObj)) {
        invalidFiles.push({
          name: fileObj.name,
          reason: "Format file tidak didukung",
        });
      } else {
        validFiles.push(file);
      }
    });

    // Tampilkan pesan error jika ada file yang tidak valid (seharusnya tidak terjadi)
    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles
        .map((f) => `• ${f.name}: ${f.reason}`)
        .join("\n");
      swal({
        title: "File Tidak Valid",
        text: `File berikut tidak dapat diupload:\n\n${errorMessages}\n\nFormat yang didukung: PDF, Word (DOC/DOCX), Excel (XLS/XLSX), JPG`,
        icon: "error",
        button: "OK",
      });
    }

    // Hitung total ukuran dan cek apakah melebihi batas
    const totalSize = validFiles.reduce((sum, file) => {
      const fileObj = file.file || file;
      return sum + (fileObj.size || 0);
    }, 0);
    const totalSizeMB = totalSize / (1024 * 1024);
    const maxSizeMB = 1.5;
    const isExceeding = totalSizeMB > maxSizeMB;

    // Update state untuk disable tombol kirim
    setFileSizeExceeded(isExceeding);

    // Update dengan file yang valid (file yang melebihi total ukuran sudah ditolak di event handler)
    if (typeof handleModalInput === "function" && invalidFiles.length === 0) {
      handleModalInput("files", validFiles);
    }
  };

  // Handler submit modal (lanjutkan/selesaikan)
  const handleSubmitModal = async () => {
    if (!selectedInstruksi) return;
    setLoadingModal(true);

    // Validasi total ukuran file sebelum submit (mengikuti pola dari Media/index.js)
    if (modalValues.files && modalValues.files.length > 0) {
      const totalSize = modalValues.files.reduce((sum, file) => {
        const fileObj = file.file || file;
        return sum + (fileObj.size || 0);
      }, 0);
      const totalSizeMB = totalSize / (1024 * 1024);
      const maxSizeMB = 1.5;

      if (totalSizeMB > maxSizeMB) {
        swal({
          title: "Ukuran File Melebihi Batas",
          text: `Total ukuran semua file (${totalSizeMB.toFixed(
            2
          )} MB) melebihi batas maksimal ${maxSizeMB} MB.\n\nSilakan hapus beberapa file atau pilih file yang lebih kecil.`,
          icon: "error",
          button: "OK",
        });
        setLoadingModal(false);
        return;
      }
    }

    const missingFields = [];
    if (!modalValues.catatan) missingFields.push("Catatan");
    if (!modalValues.status_pelaksana) missingFields.push("Status Pelaksana");

    if (!modalValues.waktu_selesai) missingFields.push("Waktu Selesai");
    if (
      modalType === "lanjutkan" &&
      (!penerima.penerima || penerima.penerima.length === 0)
    )
      missingFields.push("Penerima");
    if (modalType === "lanjutkan" && !modalValues.waktu_diterima)
      missingFields.push("Waktu Diterima");

    if (missingFields.length > 0) {
      swal(
        "Gagal",
        `Harap isi semua nilai: ${missingFields.join(", ")}`,
        "error"
      );
      setLoadingModal(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("instruksi_tindak_lanjut", modalValues.catatan);
      formData.append("status_pelaksana", modalValues.status_pelaksana);

      formData.append("waktu_selesai", modalValues.waktu_selesai);
      formData.append("catatan_selesai", modalValues.catatan);
      if (modalValues.files && modalValues.files.length > 0) {
        // Mengikuti pola dari tugas-baru: gunakan file.file || file
        modalValues.files.forEach((dzFile) => {
          // Pastikan file yang dikirim adalah File asli (mengikuti pola tugas-baru)
          formData.append("file_tindak_lanjut", dzFile.file || dzFile);
        });
      }
      let url = "";
      let method = "post";
      if (modalType === "lanjutkan") {
        // Lanjutkan instruksi (POST)
        formData.append("tingkat_perintah", modalValues.tingkat_perintah);
        formData.append("asal_perintah_dari_id", userId);
        const penerimaList = penerima.penerima.map((p) => ({
          penerima_id: p.value,
          tingkat_perintah:
            (selectedInstruksi.instruksi?.tingkat_perintah || 1) + 1,
          status_pelaksana: modalValues.status_pelaksana,
          waktu_diterima: modalValues.waktu_diterima,
          waktu_selesai: modalValues.waktu_selesai,
          instruksi_tindak_lanjut: modalValues.catatan,
        }));
        formData.append("penerima", JSON.stringify(penerimaList));
        url = `${API_ESA_OFFICE}instruksi/${selectedInstruksi.instruksi_asal?.instruksi_id}/lanjutkan`;
        method = "post";
      } else {
        // Selesaikan instruksi (PUT)
        url = `${API_ESA_OFFICE}instruksi/selesaikan/${selectedInstruksi.instruksi_asal?.instruksi_id}/${userId}`;
        method = "put";
      }
      await axios({
        method,
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      swal(
        "Sukses",
        `Instruksi berhasil di${
          modalType === "lanjutkan" ? "lanjutkan" : "selesaikan"
        }!`,
        "success"
      );
      ambilData();
      handleCloseModal();
    } catch (err) {
      swal(
        "Gagal",
        err?.response?.data?.error || "Gagal mengirim data!",
        "error"
      );
    }
    setLoadingModal(false);
  };

  // Tambahkan fungsi untuk reset modal dan penerima
  const handleCloseModal = () => {
    setOpenModal(false);
    setPenerima({ penerima: [] });
    setTingkatPerintah("");
    setFileSizeExceeded(false); // Reset state file size exceeded
    setModalValues({
      catatan: "",
      files: [],
      penerima: [],
      tingkat_perintah: "",
      status_pelaksana: "",
      waktu_diterima: "",
      waktu_selesai: "",
    });
  };

  const handleDeleteChild = async () => {
    if (!deleteTarget) return;

    try {
      await axios.delete(
        `${API_ESA_OFFICE}instruksi/hapus/${deleteTarget.instruksi_id}/${deleteTarget.asal_perintah_dari_id}/${deleteTarget.penerima_id}/`
      );
      swal("Sukses", "Instruksi-penerima berhasil dihapus.", "success");
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      ambilData();
    } catch (err) {
      swal(
        "Gagal",
        err?.response?.data?.error || "Gagal menghapus data!",
        "error"
      );
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteInstruksi = async () => {
    if (!deleteInstruksiTarget) return;
    try {
      await axios.delete(
        `${API_ESA_OFFICE}instruksi/hapus-utama/${deleteInstruksiTarget.instruksi_id}`
      );
      swal("Sukses", "Instruksi berhasil dihapus.", "success");
      setOpenDeleteInstruksiDialog(false);
      setDeleteInstruksiTarget(null);
      ambilData();
    } catch (err) {
      swal(
        "Gagal",
        err?.response?.data?.error || "Gagal menghapus data!",
        "error"
      );
      setOpenDeleteInstruksiDialog(false);
      setDeleteInstruksiTarget(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mt={4}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ height: "100%" }}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  pt={3}
                  px={2}
                >
                  <MDTypography
                    variant="h6"
                    fontWeight="medium"
                    textTransform="capitalize"
                  >
                    Instruksi Diterima
                  </MDTypography>
                </MDBox>
                {/* Filter Tahun dan Bulan */}
                <MDBox pt={2} pb={2} px={2}>
                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        options={(() => {
                          const currentYear = new Date().getFullYear();
                          const years = [];
                          for (let i = 2025; i <= currentYear; i++) {
                            years.push({ label: i.toString(), value: i });
                          }
                          return years.reverse();
                        })()}
                        value={
                          tahun
                            ? { label: tahun.toString(), value: tahun }
                            : null
                        }
                        onChange={(event, newValue) => {
                          if (newValue) {
                            setTahun(newValue.value);
                          } else {
                            setTahun(null);
                          }
                        }}
                        getOptionLabel={(option) =>
                          typeof option === "object"
                            ? option.label
                            : String(option)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.value === value?.value
                        }
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            label="Tahun"
                            variant="standard"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        options={[
                          { label: "Semua Bulan", value: "semua" },
                          { label: "Januari", value: "1" },
                          { label: "Februari", value: "2" },
                          { label: "Maret", value: "3" },
                          { label: "April", value: "4" },
                          { label: "Mei", value: "5" },
                          { label: "Juni", value: "6" },
                          { label: "Juli", value: "7" },
                          { label: "Agustus", value: "8" },
                          { label: "September", value: "9" },
                          { label: "Oktober", value: "10" },
                          { label: "November", value: "11" },
                          { label: "Desember", value: "12" },
                        ]}
                        value={
                          bulan
                            ? bulan === "semua"
                              ? { label: "Semua Bulan", value: "semua" }
                              : {
                                  label: [
                                    "Januari",
                                    "Februari",
                                    "Maret",
                                    "April",
                                    "Mei",
                                    "Juni",
                                    "Juli",
                                    "Agustus",
                                    "September",
                                    "Oktober",
                                    "November",
                                    "Desember",
                                  ][parseInt(bulan) - 1],
                                  value: bulan.toString(), // Pastikan sebagai string
                                }
                            : null
                        }
                        onChange={(event, newValue) => {
                          if (newValue) {
                            setBulan(newValue.value);
                          } else {
                            setBulan(null);
                          }
                        }}
                        getOptionLabel={(option) =>
                          typeof option === "object"
                            ? option.label
                            : String(option)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.value === value?.value
                        }
                        renderInput={(params) => (
                          <MDInput
                            {...params}
                            label="Bulan"
                            variant="standard"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    {/* Statistik */}
                    <Grid item xs={12} sm={12} md={6}>
                      <MDBox
                        display="flex"
                        gap={2}
                        flexWrap="wrap"
                        alignItems="center"
                        height="100%"
                      >
                        <Chip
                          label={`Total: ${statistik.total_instruksi || 0}`}
                          color="info"
                          size="medium"
                          sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                        />
                        <Chip
                          label={`Proses: ${statistik.total_proses || 0}`}
                          color="warning"
                          size="medium"
                          sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                        />
                        <Chip
                          label={`Selesai: ${statistik.total_selesai || 0}`}
                          color="success"
                          size="medium"
                          sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                        />
                        <Chip
                          label={`Belum Diterima: ${
                            statistik.total_belum_diterima || 0
                          }`}
                          color="error"
                          size="medium"
                          sx={{ fontSize: "0.875rem", fontWeight: "medium" }}
                        />
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
                <MDBox pt={3} pb={2} px={2}>
                  {instruksiTerima.length === 0 && (
                    <MDTypography variant="body2" color="text">
                      Belum ada instruksi diterima.
                    </MDTypography>
                  )}
                  {isMobile ? (
                    // Mobile: Card View
                    <MDBox
                      component="ul"
                      display="flex"
                      flexDirection="column"
                      p={0}
                      m={0}
                      sx={{ listStyle: "none" }}
                    >
                      {instruksiTerima.map((item) => {
                        // Pilih icon dan warna sesuai status_pelaksana
                        const statusPelaksana =
                          item.instruksi?.status_pelaksana || "Belum";
                        let color = "info";
                        let icon = "hourglass_bottom";
                        if (statusPelaksana.toLowerCase() === "selesai") {
                          color = "success";
                          icon = "check_circle";
                        } else if (statusPelaksana.toLowerCase() === "proses") {
                          color = "warning";
                          icon = "hourglass_bottom";
                        } else if (statusPelaksana.toLowerCase() === "belum") {
                          color = "error";
                          icon = "cancel";
                        }
                        // Dokumen - dari instruksi_asal
                        let dokumenArr = [];
                        if (item.instruksi_asal?.dokumen) {
                          if (Array.isArray(item.instruksi_asal.dokumen)) {
                            dokumenArr = item.instruksi_asal.dokumen;
                          } else if (
                            typeof item.instruksi_asal.dokumen === "string" &&
                            item.instruksi_asal.dokumen.trim().startsWith("[")
                          ) {
                            try {
                              dokumenArr = JSON.parse(
                                item.instruksi_asal.dokumen
                              );
                            } catch (e) {
                              dokumenArr = [];
                            }
                          }
                        }
                        // File - dari instruksi
                        let fileArr = [];
                        if (item.instruksi?.file_tindak_lanjut) {
                          if (
                            Array.isArray(item.instruksi.file_tindak_lanjut)
                          ) {
                            fileArr = item.instruksi.file_tindak_lanjut;
                          } else if (
                            typeof item.instruksi.file_tindak_lanjut ===
                              "string" &&
                            item.instruksi.file_tindak_lanjut
                              .trim()
                              .startsWith("[")
                          ) {
                            try {
                              fileArr = JSON.parse(
                                item.instruksi.file_tindak_lanjut
                              );
                            } catch (e) {
                              fileArr = [];
                            }
                          } else if (
                            typeof item.instruksi.file_tindak_lanjut ===
                              "string" &&
                            item.instruksi.file_tindak_lanjut.length > 0
                          ) {
                            fileArr = [item.instruksi.file_tindak_lanjut];
                          }
                        }
                        return (
                          <MDBox
                            key={item.instruksi_asal?.instruksi_id}
                            component="li"
                            py={2}
                            pr={2}
                            mb={2}
                            borderRadius={2}
                            sx={{ background: "#f9f9f9", boxShadow: 1 }}
                          >
                            {/* Header: Avatar & Nama Pembuat Instruksi dengan Chip IA */}
                            <MDBox display="flex" alignItems="center" mb={1}>
                              {(() => {
                                const tingkatPerintah =
                                  item.instruksi?.tingkat_perintah || 1;
                                const showChip = tingkatPerintah !== 1;

                                return (
                                  <>
                                    {showChip && (
                                      <Tooltip
                                        title="Klik untuk info instruksi asal"
                                        arrow
                                      >
                                        <Chip
                                          label="IA"
                                          size="small"
                                          color="info"
                                          onClick={() => {
                                            setSelectedInstruksiAsal(item);
                                            setOpenInstruksiAsalDialog(true);
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                            fontSize: "0.7rem",
                                            height: "24px",
                                            minWidth: "32px",
                                            ml: 2,
                                            mr: 1,
                                            "&:hover": {
                                              backgroundColor: "info.dark",
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </>
                                );
                              })()}
                              <MDAvatar
                                src={
                                  item.instruksi?.asal_perintah_dari?.uid_RSS
                                    ? `https://smartdh.rotendaokab.go.id/uploads/${item.instruksi.asal_perintah_dari.uid_RSS}.jpg`
                                    : undefined
                                }
                                size="l"
                                sx={{
                                  mr: 3,
                                  ml:
                                    (item.instruksi?.tingkat_perintah || 1) !==
                                    1
                                      ? 0
                                      : 2,
                                }}
                              />
                              <MDBox flex={1}>
                                <Grid>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                  >
                                    {item.instruksi?.asal_perintah_dari?.nama ||
                                      "-"}
                                  </MDTypography>
                                </Grid>
                                <Grid>
                                  <MDTypography variant="caption" color="text">
                                    {"issued : " +
                                      formatDate(item.instruksi_asal?.tanggal)}
                                  </MDTypography>
                                </Grid>
                              </MDBox>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenChat(
                                    item,
                                    item.instruksi?.asal_perintah_dari
                                  );
                                }}
                                sx={{ ml: 1 }}
                              >
                                <ChatIcon fontSize="small" />
                              </IconButton>
                              <Chip
                                label={item.instruksi_asal?.prioritas || "-"}
                                sx={{
                                  ml: 3,
                                  background:
                                    PRIORITAS_COLOR[
                                      item.instruksi_asal?.prioritas
                                    ] || "#bdbdbd",
                                  color: "#fff",
                                  fontWeight: 600,
                                }}
                                size="small"
                              />
                              <MDButton
                                variant="outlined"
                                color={color}
                                iconOnly
                                circular
                                sx={{ ml: 2 }}
                              >
                                <Icon sx={{ fontWeight: "bold" }}>{icon}</Icon>
                              </MDButton>
                              <MDTypography
                                variant="caption"
                                color="text"
                                sx={{ ml: 1 }}
                              >
                                {statusPelaksana}
                              </MDTypography>
                            </MDBox>
                            {/* Konten utama, margin kiri agar bergeser ke dalam */}
                            <MDBox ml={8}>
                              {item.instruksi?.tingkat_perintah !== 1 && (
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  mb={1}
                                >
                                  <MDTypography
                                    variant="caption"
                                    color="text"
                                    fontWeight="bold"
                                    mr={1}
                                  >
                                    Asal Perintah:
                                  </MDTypography>
                                  <MDAvatar
                                    src={
                                      item.instruksi?.asal_perintah_dari
                                        ?.uid_RSS
                                        ? `https://smartdh.rotendaokab.go.id/uploads/${item.instruksi.asal_perintah_dari.uid_RSS}.jpg`
                                        : undefined
                                    }
                                    size="xs"
                                    sx={{ mr: 1 }}
                                  />
                                  <MDTypography variant="caption" color="text">
                                    {item.instruksi?.asal_perintah_dari?.nama ||
                                      "-"}
                                  </MDTypography>
                                </MDBox>
                              )}
                              <MDBox mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="bold"
                                >
                                  Isi Instruksi:
                                </MDTypography>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: item.instruksi?.isi_instruksi || "",
                                  }}
                                  style={{
                                    fontSize: 14,
                                    color: "#333",
                                    wordBreak: "break-word",
                                  }}
                                />
                              </MDBox>
                              <MDBox display="flex" alignItems="center" mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="bold"
                                  mr={1}
                                >
                                  Dokumen:
                                </MDTypography>
                                <MDBox
                                  display="flex"
                                  flexDirection="row"
                                  gap={1}
                                  flexWrap="wrap"
                                >
                                  {dokumenArr.length > 0 ? (
                                    dokumenArr.map((link, idx) => {
                                      const filename = link.split("/").pop();
                                      return (
                                        <Button
                                          key={idx}
                                          href={`${API_ESA_OFFICE_UPLOAD}${link}`}
                                          target="_blank"
                                          size="small"
                                          sx={{
                                            textTransform: "none",
                                            justifyContent: "flex-start",
                                            minWidth: 90,
                                          }}
                                          variant="outlined"
                                          startIcon={getFileIcon(filename)}
                                        >
                                          {`Dok ${idx + 1}`}
                                        </Button>
                                      );
                                    })
                                  ) : (
                                    <MDTypography
                                      variant="caption"
                                      color="text"
                                    >
                                      -
                                    </MDTypography>
                                  )}
                                </MDBox>
                              </MDBox>
                              <MDBox display="flex" alignItems="center" mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  fontWeight="bold"
                                  mr={1}
                                >
                                  File:
                                </MDTypography>
                                <MDBox
                                  display="flex"
                                  flexDirection="row"
                                  gap={1}
                                  flexWrap="wrap"
                                >
                                  {fileArr.length > 0 ? (
                                    fileArr.map((link, idx) => {
                                      const filename = link.split("/").pop();
                                      return (
                                        <Button
                                          key={idx}
                                          href={`${API_ESA_OFFICE_UPLOAD}${link}`}
                                          target="_blank"
                                          size="small"
                                          sx={{
                                            textTransform: "none",
                                            justifyContent: "flex-start",
                                            minWidth: 90,
                                          }}
                                          variant="outlined"
                                          startIcon={getFileIcon(filename)}
                                        >
                                          {`File ${idx + 1}`}
                                        </Button>
                                      );
                                    })
                                  ) : (
                                    <MDTypography
                                      variant="caption"
                                      color="text"
                                    >
                                      -
                                    </MDTypography>
                                  )}
                                </MDBox>
                              </MDBox>
                              <MDBox
                                display="flex"
                                alignItems="center"
                                gap={2}
                                mb={1}
                              >
                                <MDTypography variant="caption" color="text">
                                  Tingkat:{" "}
                                  <b>
                                    {item.instruksi?.tingkat_perintah || "-"}
                                  </b>
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  Waktu Diterima:{" "}
                                  <b>
                                    {formatDate(item.instruksi?.waktu_diterima)}
                                  </b>
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  Target Selesai:{" "}
                                  {(() => {
                                    if (!item.instruksi?.waktu_selesai)
                                      return "-";
                                    const now = new Date();
                                    const selesai = new Date(
                                      item.instruksi.waktu_selesai
                                    );
                                    const diffMs = selesai - now;
                                    if (isNaN(selesai.getTime())) return "-";
                                    if (diffMs < 0) {
                                      // Sudah lewat
                                      const diff = Math.abs(diffMs);
                                      const hari = Math.floor(
                                        diff / (1000 * 60 * 60 * 24)
                                      );
                                      const jam = Math.floor(
                                        (diff / (1000 * 60 * 60)) % 24
                                      );
                                      const menit = Math.floor(
                                        (diff / (1000 * 60)) % 60
                                      );
                                      return (
                                        <span
                                          style={{
                                            color: "#f44336",
                                            fontWeight: 600,
                                          }}
                                        >
                                          Lewat{" "}
                                          {hari > 0 ? hari + " hari " : ""}
                                          {jam > 0 ? jam + " jam " : ""}
                                          {menit} menit
                                        </span>
                                      );
                                    } else {
                                      // Belum lewat
                                      const hari = Math.floor(
                                        diffMs / (1000 * 60 * 60 * 24)
                                      );
                                      const jam = Math.floor(
                                        (diffMs / (1000 * 60 * 60)) % 24
                                      );
                                      const menit = Math.floor(
                                        (diffMs / (1000 * 60)) % 60
                                      );
                                      return (
                                        <span
                                          style={{
                                            color: "#4caf50",
                                            fontWeight: 600,
                                          }}
                                        >
                                          Sisa {hari > 0 ? hari + " hari " : ""}
                                          {jam > 0 ? jam + " jam " : ""}
                                          {menit} menit
                                        </span>
                                      );
                                    }
                                  })()}
                                </MDTypography>
                              </MDBox>
                              {/* Tombol aksi sudut kanan bawah */}
                              {(() => {
                                const statusPelaksana =
                                  item.instruksi?.status_pelaksana || "";
                                const isSelesai =
                                  statusPelaksana.toLowerCase() === "selesai";

                                // Jika status selesai, sembunyikan tombol Lanjutkan dan Selesaikan
                                if (isSelesai) {
                                  return null;
                                }

                                return (
                                  <MDBox
                                    display="flex"
                                    justifyContent="flex-end"
                                    mt={2}
                                  >
                                    <>
                                      {!item.instruksi?.waktu_diterima && (
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          size="small"
                                          sx={{ minWidth: 110, mr: 1 }}
                                          onClick={() => {
                                            if (
                                              item.instruksi
                                                ?.instruksi_penerima_id
                                            ) {
                                              handleTerima(
                                                item.instruksi
                                                  .instruksi_penerima_id
                                              );
                                            }
                                          }}
                                        >
                                          Terima
                                        </Button>
                                      )}
                                      <Button
                                        variant="contained"
                                        color="info"
                                        size="small"
                                        sx={{ minWidth: 110, mr: 1 }}
                                        onClick={() => {
                                          setSelectedInstruksi(item);
                                          setModalType("lanjutkan");
                                          setOpenModal(true);
                                          setTingkatPerintah(
                                            item.instruksi?.tingkat_perintah ||
                                              1
                                          );
                                        }}
                                      >
                                        Lanjutkan
                                      </Button>
                                      <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        sx={{ minWidth: 110 }}
                                        onClick={() => {
                                          setSelectedInstruksi(item);
                                          setModalType("selesaikan");
                                          setOpenModal(true);
                                        }}
                                      >
                                        Selesaikan
                                      </Button>
                                    </>
                                  </MDBox>
                                );
                              })()}
                              {Array.isArray(item.diteruskan_ke) &&
                                item.diteruskan_ke.length > 0 && (
                                  <MDBox mt={2}>
                                    {/* RINGKASAN AVATAR PENERIMA CHILD */}
                                    <MDBox
                                      display="flex"
                                      alignItems="center"
                                      mb={1}
                                      gap={1}
                                    >
                                      {item.diteruskan_ke
                                        .slice(0, 5)
                                        .map((child, idx) => (
                                          <MDAvatar
                                            key={child.user_id || idx}
                                            src={
                                              child.uid_RSS
                                                ? `https://smartdh.rotendaokab.go.id/uploads/${child.uid_RSS}.jpg`
                                                : undefined
                                            }
                                            size="xs"
                                            sx={{
                                              border: "2px solid #fff",
                                              boxShadow: 1,
                                              ml: idx === 0 ? 0 : -1,
                                            }}
                                          />
                                        ))}
                                      {item.diteruskan_ke.length > 5 && (
                                        <MDBox
                                          sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: "50%",
                                            bgcolor: "grey.300",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 13,
                                            fontWeight: "bold",
                                            ml: -1,
                                          }}
                                        >
                                          +{item.diteruskan_ke.length - 5}
                                        </MDBox>
                                      )}
                                      {/* PROGRESS BAR & STATUS */}
                                      {(() => {
                                        const total = item.diteruskan_ke.length;
                                        const selesai =
                                          item.diteruskan_ke.filter(
                                            (c) =>
                                              (c.status || "").toLowerCase() ===
                                              "selesai"
                                          ).length;
                                        const progress =
                                          total > 0
                                            ? Math.round(
                                                (selesai / total) * 100
                                              )
                                            : 0;
                                        let statusLabel = "Belum";
                                        let statusColor = "#bdbdbd";
                                        let statusIcon = (
                                          <CancelIcon
                                            sx={{
                                              color: "#bdbdbd",
                                              fontSize: 20,
                                              ml: 1,
                                            }}
                                          />
                                        );
                                        if (selesai === 0) {
                                          statusLabel = "Belum";
                                          statusColor = "#bdbdbd";
                                          statusIcon = (
                                            <CancelIcon
                                              sx={{
                                                color: "#bdbdbd",
                                                fontSize: 20,
                                                ml: 1,
                                              }}
                                            />
                                          );
                                        } else if (
                                          selesai > 0 &&
                                          selesai < total
                                        ) {
                                          statusLabel = "On Progress";
                                          statusColor = "#ffb300";
                                          statusIcon = (
                                            <HourglassBottomIcon
                                              sx={{
                                                color: "#ffb300",
                                                fontSize: 20,
                                                ml: 1,
                                              }}
                                            />
                                          );
                                        } else if (
                                          selesai === total &&
                                          total > 0
                                        ) {
                                          statusLabel = "Selesai";
                                          statusColor = "#4caf50";
                                          statusIcon = (
                                            <CheckCircleIcon
                                              sx={{
                                                color: "#4caf50",
                                                fontSize: 20,
                                                ml: 1,
                                              }}
                                            />
                                          );
                                        }
                                        return (
                                          <MDBox
                                            display="flex"
                                            alignItems="center"
                                            ml={2}
                                            minWidth={120}
                                          >
                                            <MDBox flex={1} minWidth={60}>
                                              <MDBox
                                                sx={{
                                                  width: "100%",
                                                  background: "#eee",
                                                  borderRadius: 8,
                                                  height: 10,
                                                  overflow: "hidden",
                                                }}
                                              >
                                                <MDBox
                                                  sx={{
                                                    width: `${progress}%`,
                                                    background: statusColor,
                                                    height: "100%",
                                                    borderRadius: 8,
                                                    transition: "width 0.5s",
                                                  }}
                                                />
                                              </MDBox>
                                            </MDBox>
                                            <MDTypography
                                              variant="caption"
                                              color="text"
                                              sx={{ minWidth: 36, ml: 1 }}
                                            >
                                              {progress}%
                                            </MDTypography>
                                            <MDTypography
                                              variant="caption"
                                              color="text"
                                              sx={{ ml: 1, fontWeight: 600 }}
                                            >
                                              {statusLabel}
                                            </MDTypography>
                                            {statusIcon}
                                          </MDBox>
                                        );
                                      })()}
                                      {/* TOMBOL EXPAND/COLLAPSE */}
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setOpenDiteruskan((prev) => ({
                                            ...prev,
                                            [item.instruksi_id]:
                                              !prev[item.instruksi_id],
                                          }))
                                        }
                                        sx={{
                                          ml: 1,
                                          transition: "transform 0.2s",
                                          transform: openDiteruskan[
                                            item.instruksi_id
                                          ]
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        }}
                                      >
                                        <ExpandMoreIcon />
                                      </IconButton>
                                    </MDBox>
                                    {/* COLLAPSE DETAIL CHILD */}
                                    <Collapse
                                      in={!!openDiteruskan[item.instruksi_id]}
                                    >
                                      {item.diteruskan_ke.map((child, idx) => (
                                        <Card
                                          key={child.user_id || idx}
                                          sx={{
                                            p: 2,
                                            mb: 1,
                                            boxShadow: 1,
                                            position: "relative",
                                          }}
                                        >
                                          <MDBox
                                            display="flex"
                                            alignItems="center"
                                          >
                                            <MDAvatar
                                              src={
                                                child.uid_RSS
                                                  ? `https://smartdh.rotendaokab.go.id/uploads/${child.uid_RSS}.jpg`
                                                  : undefined
                                              }
                                              size="sm"
                                              sx={{ mr: 2 }}
                                            />
                                            <MDTypography
                                              variant="button"
                                              fontWeight="bold"
                                            >
                                              {child.nama}
                                            </MDTypography>
                                            <Chip
                                              label={child.status}
                                              size="small"
                                              sx={{ ml: 2 }}
                                            />
                                          </MDBox>
                                          <MDBox mt={1}>
                                            <MDTypography
                                              variant="caption"
                                              color="text"
                                            >
                                              Tingkat: {child.tingkat} | Waktu
                                              Diterima:{" "}
                                              {formatDate(child.waktu_diterima)}{" "}
                                              | Target Selesai:{" "}
                                              {(() => {
                                                if (!child.waktu_selesai)
                                                  return "-";
                                                const now = new Date();
                                                const selesai = new Date(
                                                  child.waktu_selesai
                                                );
                                                const diffMs = selesai - now;
                                                if (isNaN(selesai.getTime()))
                                                  return "-";
                                                if (diffMs < 0) {
                                                  // Sudah lewat
                                                  const diff = Math.abs(diffMs);
                                                  const hari = Math.floor(
                                                    diff / (1000 * 60 * 60 * 24)
                                                  );
                                                  const jam = Math.floor(
                                                    (diff / (1000 * 60 * 60)) %
                                                      24
                                                  );
                                                  const menit = Math.floor(
                                                    (diff / (1000 * 60)) % 60
                                                  );
                                                  return (
                                                    <span
                                                      style={{
                                                        color: "#f44336",
                                                        fontWeight: 600,
                                                      }}
                                                    >
                                                      Lewat{" "}
                                                      {hari > 0
                                                        ? hari + " hari "
                                                        : ""}
                                                      {jam > 0
                                                        ? jam + " jam "
                                                        : ""}
                                                      {menit} menit
                                                    </span>
                                                  );
                                                } else {
                                                  // Belum lewat
                                                  const hari = Math.floor(
                                                    diffMs /
                                                      (1000 * 60 * 60 * 24)
                                                  );
                                                  const jam = Math.floor(
                                                    (diffMs /
                                                      (1000 * 60 * 60)) %
                                                      24
                                                  );
                                                  const menit = Math.floor(
                                                    (diffMs / (1000 * 60)) % 60
                                                  );
                                                  return (
                                                    <span
                                                      style={{
                                                        color: "#4caf50",
                                                        fontWeight: 600,
                                                      }}
                                                    >
                                                      Sisa{" "}
                                                      {hari > 0
                                                        ? hari + " hari "
                                                        : ""}
                                                      {jam > 0
                                                        ? jam + " jam "
                                                        : ""}
                                                      {menit} menit
                                                    </span>
                                                  );
                                                }
                                              })()}
                                            </MDTypography>
                                          </MDBox>
                                          {/* File dan instruksi_tindak_lanjut */}
                                          {child.file && (
                                            <MDBox mt={1}>
                                              <MDTypography
                                                variant="caption"
                                                color="text"
                                                fontWeight="bold"
                                              >
                                                File:
                                              </MDTypography>
                                              <MDBox
                                                display="flex"
                                                flexDirection="row"
                                                gap={1}
                                                flexWrap="wrap"
                                              >
                                                {(() => {
                                                  let fileArr = [];
                                                  if (Array.isArray(child.file))
                                                    fileArr = child.file;
                                                  else if (
                                                    typeof child.file ===
                                                      "string" &&
                                                    child.file
                                                      .trim()
                                                      .startsWith("[")
                                                  ) {
                                                    try {
                                                      fileArr = JSON.parse(
                                                        child.file
                                                      );
                                                    } catch (e) {
                                                      fileArr = [];
                                                    }
                                                  } else if (
                                                    typeof child.file ===
                                                      "string" &&
                                                    child.file.length > 0
                                                  ) {
                                                    fileArr = [child.file];
                                                  }
                                                  return fileArr.length > 0 ? (
                                                    fileArr.map((link, idx) => {
                                                      const filename = link
                                                        .split("/")
                                                        .pop();
                                                      return (
                                                        <Button
                                                          key={idx}
                                                          href={`${API_ESA_OFFICE_UPLOAD}${link}`}
                                                          target="_blank"
                                                          size="small"
                                                          sx={{
                                                            textTransform:
                                                              "none",
                                                            justifyContent:
                                                              "flex-start",
                                                            minWidth: 90,
                                                          }}
                                                          variant="outlined"
                                                          startIcon={getFileIcon(
                                                            filename
                                                          )}
                                                        >
                                                          {`File ${idx + 1}`}
                                                        </Button>
                                                      );
                                                    })
                                                  ) : (
                                                    <MDTypography
                                                      variant="caption"
                                                      color="text"
                                                    >
                                                      -
                                                    </MDTypography>
                                                  );
                                                })()}
                                              </MDBox>
                                            </MDBox>
                                          )}
                                          {child.instruksi_tindak_lanjut && (
                                            <MDBox mt={1}>
                                              <MDTypography
                                                variant="caption"
                                                color="text"
                                                fontWeight="bold"
                                              >
                                                Instruksi Tindak Lanjut:
                                              </MDTypography>
                                              <MDTypography
                                                variant="caption"
                                                color="text"
                                              >
                                                {child.instruksi_tindak_lanjut}
                                              </MDTypography>
                                            </MDBox>
                                          )}
                                          {/* Icon Delete kanan bawah */}
                                          <IconButton
                                            size="small"
                                            color="error"
                                            sx={{
                                              position: "absolute",
                                              right: 8,
                                              bottom: 8,
                                            }}
                                            onClick={() => {
                                              setDeleteTarget({
                                                instruksi_id: item.instruksi_id,
                                                asal_perintah_dari_id: userId,
                                                penerima_id: child.user_id,
                                                nama_penerima: child.nama,
                                              });
                                              setOpenDeleteDialog(true);
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Card>
                                      ))}
                                    </Collapse>
                                  </MDBox>
                                )}
                            </MDBox>
                          </MDBox>
                        );
                      })}
                    </MDBox>
                  ) : (
                    // Desktop: DataTable View
                    <DataTable
                      table={{
                        columns: [
                          {
                            Header: "No.",
                            accessor: "no",
                            width: "5%",
                            Cell: ({ row }) => (
                              <MDTypography variant="caption" color="text">
                                {row.index + 1}
                              </MDTypography>
                            ),
                          },
                          {
                            Header: "Pembuat Instruksi",
                            accessor: "instruksi.asal_perintah_dari",
                            width: "15%",
                            Cell: ({ row }) => {
                              const tingkatPerintah =
                                row.original.instruksi?.tingkat_perintah || 1;
                              const showChip = tingkatPerintah !== 1;

                              return (
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                >
                                  {showChip && (
                                    <Tooltip
                                      title="Klik untuk info instruksi asal"
                                      arrow
                                    >
                                      <Chip
                                        label="IA"
                                        size="small"
                                        color="info"
                                        onClick={() => {
                                          setSelectedInstruksiAsal(
                                            row.original
                                          );
                                          setOpenInstruksiAsalDialog(true);
                                        }}
                                        sx={{
                                          cursor: "pointer",
                                          fontSize: "0.7rem",
                                          height: "22px",
                                          minWidth: "32px",
                                          "&:hover": {
                                            backgroundColor: "info.dark",
                                          },
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    flex={1}
                                  >
                                    <MDAvatar
                                      src={
                                        row.original.instruksi
                                          ?.asal_perintah_dari?.uid_RSS
                                          ? `https://smartdh.rotendaokab.go.id/uploads/${row.original.instruksi.asal_perintah_dari.uid_RSS}.jpg`
                                          : undefined
                                      }
                                      alt={
                                        row.original.instruksi
                                          ?.asal_perintah_dari?.nama || "-"
                                      }
                                      size="sm"
                                      sx={{ mr: 1 }}
                                    />
                                    <MDTypography
                                      variant="caption"
                                      color="text"
                                    >
                                      {row.original.instruksi
                                        ?.asal_perintah_dari?.nama || "-"}
                                    </MDTypography>
                                  </MDBox>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenChat(
                                        row.original,
                                        row.original.instruksi
                                          ?.asal_perintah_dari
                                      );
                                    }}
                                    sx={{ ml: 0.5 }}
                                  >
                                    <ChatIcon fontSize="small" />
                                  </IconButton>
                                </MDBox>
                              );
                            },
                          },
                          {
                            Header: "Tanggal",
                            accessor: "instruksi_asal.tanggal",
                            width: "10%",
                            Cell: ({ row }) => (
                              <MDTypography variant="caption" color="text">
                                {row.original.instruksi_asal?.tanggal
                                  ? formatDate(
                                      row.original.instruksi_asal.tanggal
                                    )
                                  : "-"}
                              </MDTypography>
                            ),
                          },
                          {
                            Header: "Prioritas",
                            accessor: "instruksi_asal.prioritas",
                            width: "8%",
                            Cell: ({ row }) => (
                              <Chip
                                label={
                                  row.original.instruksi_asal?.prioritas || "-"
                                }
                                size="small"
                                sx={{
                                  background:
                                    PRIORITAS_COLOR[
                                      row.original.instruksi_asal?.prioritas
                                    ] || "#bdbdbd",
                                  color: "#fff",
                                  fontWeight: 600,
                                }}
                              />
                            ),
                          },
                          {
                            Header: "Isi Instruksi",
                            accessor: "instruksi.isi_instruksi",
                            width: "25%",
                            Cell: ({ row }) => {
                              const isiInstruksi =
                                row.original.instruksi?.isi_instruksi || "";
                              return (
                                <MDBox
                                  sx={{
                                    whiteSpace: "normal",
                                    wordWrap: "break-word",
                                    maxWidth: "300px",
                                    py: 1,
                                  }}
                                >
                                  <MDTypography
                                    variant="caption"
                                    color="text"
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        isiInstruksi &&
                                        isiInstruksi.length > 100
                                          ? isiInstruksi.substring(0, 100) +
                                            "..."
                                          : isiInstruksi || "-",
                                    }}
                                  />
                                </MDBox>
                              );
                            },
                          },
                          {
                            Header: "File",
                            accessor: "instruksi.file_tindak_lanjut",
                            width: "10%",
                            Cell: ({ row }) => renderFile(row),
                          },
                          {
                            Header: "Waktu Selesai",
                            accessor: "instruksi.waktu_selesai",
                            width: "10%",
                            Cell: ({ row }) => (
                              <MDTypography variant="caption" color="text">
                                {row.original.instruksi?.waktu_selesai
                                  ? formatDate(
                                      row.original.instruksi.waktu_selesai
                                    )
                                  : "-"}
                              </MDTypography>
                            ),
                          },
                          {
                            Header: "Status",
                            accessor: "status_pelaksana",
                            width: "15%",
                            Cell: ({ row }) => renderStatus(row.original),
                          },
                          {
                            Header: "Tindak Lanjut",
                            accessor: "tindak_lanjut",
                            width: "12%",
                            Cell: ({ row }) => renderTindakLanjut(row.original),
                          },
                          {
                            Header: "Dilanjutkan",
                            accessor: "diteruskan_ke",
                            width: "10%",
                            Cell: ({ row }) => renderDilanjutkan(row.original),
                          },
                        ],
                        rows: instruksiTerima,
                      }}
                      entriesPerPage={{
                        defaultValue: 10,
                        entries: [5, 10, 15, 20, 25],
                      }}
                      canSearch
                      showTotalEntries
                      pagination
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      {/* Modal Form Lanjutkan/Selesaikan */}

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalType === "lanjutkan"
            ? "Lanjutkan Instruksi ke Level Bawah"
            : "Selesaikan Instruksi"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={
              modalType === "lanjutkan"
                ? "Catatan/Instruksi"
                : "Catatan Penyelesaian/Laporan"
            }
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 2 }}
            value={modalValues.catatan}
            onChange={(e) => handleModalInput("catatan", e.target.value)}
          />
          <MDBox mt={1}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDBox my={2} display="inline-block">
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    Tags
                  </MDTypography>
                </MDBox>
                {/* Autocomplete hanya tampil jika modalType !== 'selesaikan' */}
                {modalType !== "selesaikan" && (
                  <Autocomplete
                    multiple
                    options={allOptions}
                    value={penerima.penerima || []}
                    onChange={handleChange}
                    getOptionLabel={(option) => option.label}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        {option.value === "all" ? (
                          <strong style={{ color: "#1976d2" }}>
                            Pilih Semua
                          </strong>
                        ) : (
                          <>
                            <MDAvatar
                              src={option.avatar}
                              alt={option.label}
                              size="xs"
                              sx={{ mr: 1 }}
                            />
                            {option.label}
                          </>
                        )}
                      </li>
                    )}
                    renderTags={(selected, getTagProps) =>
                      selected.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          avatar={
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <MDAvatar
                                src={option.avatar}
                                alt={option.label}
                                size="xxs"
                                sx={{ width: 24, height: 24, mr: 1 }}
                              />
                              <span style={{ marginLeft: 4 }} />
                            </span>
                          }
                          label={option.label}
                          style={{
                            marginRight: 4,
                            marginBottom: 2,
                            display: "flex",
                            alignItems: "center",
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        variant="standard"
                        label="Penerima Instruksi"
                      />
                    )}
                  />
                )}
              </Grid>
            </Grid>
          </MDBox>

          <MDBox mt={2} mb={1}>
            <MDBox mb={2} display="inline-block">
              <MDTypography
                component="label"
                variant="button"
                fontWeight="regular"
                color="text"
                textTransform="capitalize"
              >
                Status Pelaksana
              </MDTypography>
            </MDBox>
            <Autocomplete
              value={modalValues.status_pelaksana || ""}
              onChange={(e, value) =>
                handleModalInput("status_pelaksana", value)
              }
              options={["belum", "proses", "selesai"]}
              renderInput={(params) => (
                <MDInput
                  {...params}
                  variant="standard"
                  label="Pilih Status Pelaksana"
                />
              )}
            />
          </MDBox>

          {/* Input status, waktu instruksi, waktu selesai hanya tampil jika bukan selesaikan */}
          {modalType !== "selesaikan" && (
            <TextField
              label="Waktu Instruksi"
              type="datetime-local"
              fullWidth
              sx={{ mt: 2 }}
              value={modalValues.waktu_diterima}
              onChange={(e) =>
                handleModalInput("waktu_diterima", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
          )}
          <TextField
            label="Waktu Selesai"
            type="datetime-local"
            fullWidth
            sx={{ mt: 2 }}
            value={modalValues.waktu_selesai}
            onChange={(e) => handleModalInput("waktu_selesai", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <MDBox mt={2}>
            <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <MDTypography
                component="label"
                variant="button"
                fontWeight="regular"
                color="text"
              >
                Upload dokumen, jika ada (bisa lebih dari satu file)
                <br />
                <MDTypography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  Format: PDF, Word, Excel, JPG | Maksimal total: 1.5 MB untuk
                  semua file
                </MDTypography>
              </MDTypography>
            </MDBox>
            {useMemo(
              () => (
                <MDDropzone
                  options={{
                    addRemoveLinks: true,
                    uploadMultiple: true,
                    maxTotalFilesize: 1.5, // Maksimal 1.5 MB total untuk semua file
                    acceptedFiles:
                      ".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.jpg,.jpeg", // PDF, Word, Excel, JPG
                    dictDefaultMessage:
                      "Klik atau seret file ke sini untuk mengunggah",
                    dictTotalFilesTooBig:
                      "Total ukuran semua file melebihi batas maksimal 1.5 MB",
                    dictInvalidFileType:
                      "Format file tidak didukung. Hanya PDF, Word (DOC/DOCX), Excel (XLS/XLSX), dan JPG yang diperbolehkan",
                    dictResponseError: "Terjadi kesalahan saat mengunggah file",
                    dictCancelUpload: "Batalkan unggah",
                    dictUploadCanceled: "Unggah dibatalkan",
                    dictCancelUploadConfirmation:
                      "Apakah Anda yakin ingin membatalkan unggah?",
                    dictRemoveFile: "Hapus file",
                  }}
                  onChange={handleDropzoneChange}
                  ref={dropzoneRef}
                />
              ),
              []
            )}
            <MDBox mt={1}>
              <MDTypography variant="caption" color="text.secondary">
                * Format yang didukung: PDF, Word (DOC/DOCX), Excel (XLS/XLSX),
                JPG
                <br />* Maksimal total ukuran semua file: 1.5 MB
              </MDTypography>
            </MDBox>
          </MDBox>
          {/* Preview file yang sudah diupload */}
          {modalValues.files &&
            modalValues.files.length > 0 &&
            (() => {
              // Hitung total ukuran
              const totalSize = modalValues.files.reduce((sum, file) => {
                const fileObj = file.file || file;
                return sum + (fileObj.size || 0);
              }, 0);
              const totalSizeMB = totalSize / (1024 * 1024);
              const maxSizeMB = 1.5;
              const isExceeding = totalSizeMB > maxSizeMB;

              return (
                <div style={{ marginBottom: 16, marginTop: 16 }}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      color="text"
                    >
                      File terupload:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color={isExceeding ? "error" : "text.secondary"}
                      fontWeight="medium"
                    >
                      Total: {totalSizeMB.toFixed(2)} MB / {maxSizeMB} MB
                    </MDTypography>
                  </MDBox>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: 8,
                      fontSize: 13,
                      fontFamily: "inherit, Arial, sans-serif",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#f5f5f5" }}>
                        <th
                          style={{
                            border: "1px solid #ddd",
                            padding: 8,
                            textAlign: "center",
                            fontWeight: 700,
                            fontSize: 13,
                            fontFamily: "inherit, Arial, sans-serif",
                          }}
                        >
                          No
                        </th>
                        <th
                          style={{
                            border: "1px solid #ddd",
                            padding: 8,
                            textAlign: "left",
                            fontWeight: 700,
                            fontSize: 13,
                            fontFamily: "inherit, Arial, sans-serif",
                          }}
                        >
                          Nama File
                        </th>
                        <th
                          style={{
                            border: "1px solid #ddd",
                            padding: 8,
                            textAlign: "right",
                            fontWeight: 700,
                            fontSize: 13,
                            fontFamily: "inherit, Arial, sans-serif",
                          }}
                        >
                          Ukuran
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalValues.files.map((file, idx) => {
                        const fileObj = file.file || file;
                        return (
                          <tr key={idx}>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: 8,
                                textAlign: "center",
                                fontWeight: 400,
                                fontSize: 12,
                                fontFamily: "inherit, Arial, sans-serif",
                              }}
                            >
                              {idx + 1}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: 8,
                                fontWeight: 400,
                                fontSize: 12,
                                fontFamily: "inherit, Arial, sans-serif",
                              }}
                            >
                              {fileObj.name || "Unknown"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: 8,
                                textAlign: "right",
                                fontWeight: 400,
                                fontSize: 12,
                                fontFamily: "inherit, Arial, sans-serif",
                              }}
                            >
                              {fileObj.size
                                ? `${(fileObj.size / 1024).toFixed(2)} KB`
                                : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            color="secondary"
            disabled={loadingModal}
          >
            Batal
          </Button>
          <Button
            onClick={handleSubmitModal}
            color="primary"
            variant="contained"
            disabled={loadingModal || fileSizeExceeded}
          >
            {loadingModal ? "Mengirim..." : "Kirim"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk menampilkan file PDF */}
      <Dialog
        open={openFileDialog}
        onClose={handleCloseFileDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>File</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {selectedFile && (
              <iframe
                src={selectedFile}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
              />
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileDialog}>Tutup</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Pilihan Tindak Lanjut */}
      <Dialog
        open={openPilihanDialog}
        onClose={handleClosePilihanDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center">
            <SendIcon sx={{ mr: 1 }} />
            Pilih Tindak Lanjut
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDTypography variant="h6" mb={2}>
                  Detail Instruksi
                </MDTypography>
                <MDBox mt={2}>
                  <MDTypography variant="body2">
                    <strong>Pengirim:</strong>{" "}
                    {selectedInstruksiTL?.instruksi_asal?.pengirim?.nama || "-"}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Tanggal:</strong>{" "}
                    {selectedInstruksiTL?.instruksi_asal?.tanggal
                      ? formatDate(selectedInstruksiTL.instruksi_asal.tanggal)
                      : "-"}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Isi Instruksi:</strong>{" "}
                    {selectedInstruksiTL?.instruksi?.isi_instruksi ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: selectedInstruksiTL.instruksi.isi_instruksi,
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="h6" mb={2}>
                  Pilih Tindak Lanjut
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={() => handlePilihTindakLanjut("selesai")}
                    startIcon={<CheckCircleIcon />}
                  >
                    Selesaikan Instruksi
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    fullWidth
                    onClick={() => handlePilihTindakLanjut("lanjutkan")}
                    startIcon={<SendIcon />}
                  >
                    Lanjutkan ke Penerima Lain
                  </Button>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePilihanDialog}>Batal</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk Tindak Lanjut (Selesaikan) */}
      <Dialog
        open={openTindakLanjutDialog}
        onClose={handleCloseTindakLanjut}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Selesaikan Instruksi</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDTypography variant="h6">Detail Instruksi</MDTypography>
                <MDBox mt={2}>
                  <MDTypography variant="body2">
                    <strong>Pengirim:</strong>{" "}
                    {selectedInstruksiTL?.instruksi_asal?.pengirim?.nama || "-"}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Tanggal:</strong>{" "}
                    {selectedInstruksiTL?.instruksi_asal?.tanggal
                      ? formatDate(selectedInstruksiTL.instruksi_asal.tanggal)
                      : "-"}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Isi Instruksi:</strong>{" "}
                    {selectedInstruksiTL?.instruksi?.isi_instruksi ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: selectedInstruksiTL.instruksi.isi_instruksi,
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDDatePicker
                  input={{
                    label: "Tanggal Tindak Lanjut",
                    variant: "standard",
                    fullWidth: true,
                  }}
                  value={tanggalTL}
                  onChange={(date) => setTanggalTL(date)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Keterangan Tindak Lanjut"
                  multiline
                  rows={4}
                  fullWidth
                  value={keteranganTL}
                  onChange={(e) => setKeteranganTL(e.target.value)}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12}>
                <MDBox mt={2}>
                  <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <MDTypography
                      component="label"
                      variant="button"
                      fontWeight="regular"
                      color="text"
                    >
                      Upload dokumen, jika ada (bisa lebih dari satu file)
                      <br />
                      <MDTypography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        Format: PDF, Word, Excel, JPG | Maksimal total: 1.5 MB
                        untuk semua file
                      </MDTypography>
                    </MDTypography>
                  </MDBox>
                  {useMemo(
                    () => (
                      <MDDropzone
                        options={{
                          addRemoveLinks: true,
                          uploadMultiple: true,
                          maxTotalFilesize: 1.5, // Maksimal 1.5 MB total untuk semua file
                          acceptedFiles:
                            ".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.jpg,.jpeg", // PDF, Word, Excel, JPG
                          dictDefaultMessage:
                            "Klik atau seret file ke sini untuk mengunggah",
                          dictTotalFilesTooBig:
                            "Total ukuran semua file melebihi batas maksimal 1.5 MB",
                          dictInvalidFileType:
                            "Format file tidak didukung. Hanya PDF, Word (DOC/DOCX), Excel (XLS/XLSX), dan JPG yang diperbolehkan",
                          dictResponseError:
                            "Terjadi kesalahan saat mengunggah file",
                          dictCancelUpload: "Batalkan unggah",
                          dictUploadCanceled: "Unggah dibatalkan",
                          dictCancelUploadConfirmation:
                            "Apakah Anda yakin ingin membatalkan unggah?",
                          dictRemoveFile: "Hapus file",
                        }}
                        onChange={handleDropzoneChangeTL}
                        ref={dropzoneRefTL}
                      />
                    ),
                    []
                  )}
                  <MDBox mt={1}>
                    <MDTypography variant="caption" color="text.secondary">
                      * Format yang didukung: PDF, Word (DOC/DOCX), Excel
                      (XLS/XLSX), JPG
                      <br />* Maksimal total ukuran semua file: 1.5 MB
                    </MDTypography>
                  </MDBox>
                </MDBox>
                {/* Preview file yang sudah diupload */}
                {filesTL &&
                  filesTL.length > 0 &&
                  (() => {
                    // Hitung total ukuran
                    const totalSize = filesTL.reduce((sum, file) => {
                      const fileObj = file.file || file;
                      return sum + (fileObj.size || 0);
                    }, 0);
                    const totalSizeMB = totalSize / (1024 * 1024);
                    const maxSizeMB = 1.5;
                    const isExceeding = totalSizeMB > maxSizeMB;

                    return (
                      <div style={{ marginBottom: 16, marginTop: 16 }}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="text"
                          >
                            File terupload:
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color={isExceeding ? "error" : "text.secondary"}
                            fontWeight="medium"
                          >
                            Total: {totalSizeMB.toFixed(2)} MB / {maxSizeMB} MB
                          </MDTypography>
                        </MDBox>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            marginTop: 8,
                            fontSize: 13,
                            fontFamily: "inherit, Arial, sans-serif",
                          }}
                        >
                          <thead>
                            <tr style={{ background: "#f5f5f5" }}>
                              <th
                                style={{
                                  border: "1px solid #ddd",
                                  padding: 8,
                                  textAlign: "center",
                                  fontWeight: 700,
                                  fontSize: 13,
                                  fontFamily: "inherit, Arial, sans-serif",
                                }}
                              >
                                No
                              </th>
                              <th
                                style={{
                                  border: "1px solid #ddd",
                                  padding: 8,
                                  textAlign: "left",
                                  fontWeight: 700,
                                  fontSize: 13,
                                  fontFamily: "inherit, Arial, sans-serif",
                                }}
                              >
                                Nama File
                              </th>
                              <th
                                style={{
                                  border: "1px solid #ddd",
                                  padding: 8,
                                  textAlign: "right",
                                  fontWeight: 700,
                                  fontSize: 13,
                                  fontFamily: "inherit, Arial, sans-serif",
                                }}
                              >
                                Ukuran
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filesTL.map((file, idx) => {
                              const fileObj = file.file || file;
                              return (
                                <tr key={idx}>
                                  <td
                                    style={{
                                      border: "1px solid #ddd",
                                      padding: 8,
                                      textAlign: "center",
                                      fontWeight: 400,
                                      fontSize: 12,
                                      fontFamily: "inherit, Arial, sans-serif",
                                    }}
                                  >
                                    {idx + 1}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #ddd",
                                      padding: 8,
                                      fontWeight: 400,
                                      fontSize: 12,
                                      fontFamily: "inherit, Arial, sans-serif",
                                    }}
                                  >
                                    {fileObj.name || "Unknown"}
                                  </td>
                                  <td
                                    style={{
                                      border: "1px solid #ddd",
                                      padding: 8,
                                      textAlign: "right",
                                      fontWeight: 400,
                                      fontSize: 12,
                                      fontFamily: "inherit, Arial, sans-serif",
                                    }}
                                  >
                                    {fileObj.size
                                      ? `${(fileObj.size / 1024).toFixed(2)} KB`
                                      : "-"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTindakLanjut} disabled={loadingModal}>
            Batal
          </Button>
          <Button
            onClick={handleSubmitTindakLanjut}
            variant="contained"
            color="success"
            disabled={showSizeWarningTL || !keteranganTL || loadingModal}
          >
            {loadingModal ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Daftar Penerima Diteruskan */}
      <Dialog
        open={openDiteruskanDialog}
        onClose={() => {
          setOpenDiteruskanDialog(false);
          setSelectedDiteruskanData(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Daftar Penerima Diteruskan</MDTypography>
            <IconButton
              onClick={() => {
                setOpenDiteruskanDialog(false);
                setSelectedDiteruskanData(null);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {selectedDiteruskanData &&
          Array.isArray(selectedDiteruskanData.diteruskan_ke) &&
          selectedDiteruskanData.diteruskan_ke.length > 0 ? (
            <MDBox>
              {selectedDiteruskanData.diteruskan_ke.map((child, idx) => {
                // Parse file
                let fileArr = [];
                if (Array.isArray(child.file)) {
                  fileArr = child.file;
                } else if (
                  typeof child.file === "string" &&
                  child.file.trim().startsWith("[")
                ) {
                  try {
                    fileArr = JSON.parse(child.file);
                  } catch (e) {
                    fileArr = [];
                  }
                } else if (
                  typeof child.file === "string" &&
                  child.file.length > 0
                ) {
                  fileArr = [child.file];
                }

                return (
                  <Card
                    key={child.user_id || idx}
                    sx={{
                      mb: 2,
                      p: 2,
                      boxShadow: 1,
                      position: "relative",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" mb={1}>
                      <MDAvatar
                        src={
                          child.uid_RSS
                            ? `https://smartdh.rotendaokab.go.id/uploads/${child.uid_RSS}.jpg`
                            : undefined
                        }
                        size="sm"
                        sx={{ mr: 2 }}
                      />
                      <MDBox flex={1}>
                        <MDTypography variant="button" fontWeight="bold">
                          {child.nama}
                        </MDTypography>
                        <MDBox display="flex" gap={2} mt={0.5}>
                          <MDTypography variant="caption" color="text">
                            Tingkat: {child.tingkat}
                          </MDTypography>
                          <Chip
                            label={child.status || "Belum"}
                            size="small"
                            color={
                              (child.status || "").toLowerCase() === "selesai"
                                ? "success"
                                : (child.status || "").toLowerCase() ===
                                  "proses"
                                ? "warning"
                                : "error"
                            }
                          />
                        </MDBox>
                      </MDBox>
                    </MDBox>

                    <MDBox mt={1}>
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontWeight="bold"
                        display="block"
                        mb={0.5}
                      >
                        Waktu Diterima:
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {child.waktu_diterima
                          ? formatDate(child.waktu_diterima)
                          : "-"}
                      </MDTypography>
                    </MDBox>

                    {child.waktu_selesai && (
                      <MDBox mt={1}>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="bold"
                          display="block"
                          mb={0.5}
                        >
                          Waktu Selesai:
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {formatDate(child.waktu_selesai)}
                        </MDTypography>
                      </MDBox>
                    )}

                    {child.instruksi_tindak_lanjut && (
                      <MDBox mt={1}>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="bold"
                          display="block"
                          mb={0.5}
                        >
                          Instruksi Tindak Lanjut:
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {child.instruksi_tindak_lanjut}
                        </MDTypography>
                      </MDBox>
                    )}

                    {fileArr.length > 0 && (
                      <MDBox mt={1}>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="bold"
                          display="block"
                          mb={0.5}
                        >
                          File:
                        </MDTypography>
                        <MDBox
                          display="flex"
                          flexDirection="row"
                          gap={1}
                          flexWrap="wrap"
                        >
                          {fileArr.map((link, fileIdx) => {
                            const filename = link.split("/").pop();
                            return (
                              <Button
                                key={fileIdx}
                                href={`${API_ESA_OFFICE_UPLOAD}${link}`}
                                target="_blank"
                                size="small"
                                sx={{
                                  textTransform: "none",
                                  justifyContent: "flex-start",
                                  minWidth: 90,
                                }}
                                variant="outlined"
                                startIcon={getFileIcon(filename)}
                              >
                                {`File ${fileIdx + 1}`}
                              </Button>
                            );
                          })}
                        </MDBox>
                      </MDBox>
                    )}
                  </Card>
                );
              })}
            </MDBox>
          ) : (
            <MDTypography variant="body2" color="text">
              Tidak ada data diteruskan
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDiteruskanDialog(false);
              setSelectedDiteruskanData(null);
            }}
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Informasi Instruksi Asal */}
      <Dialog
        open={openInstruksiAsalDialog}
        onClose={() => {
          setOpenInstruksiAsalDialog(false);
          setSelectedInstruksiAsal(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Informasi Instruksi Asal</MDTypography>
            <IconButton
              onClick={() => {
                setOpenInstruksiAsalDialog(false);
                setSelectedInstruksiAsal(null);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {selectedInstruksiAsal?.instruksi_asal ? (
            <MDBox>
              <Card sx={{ p: 2, mb: 2 }}>
                <MDBox display="flex" alignItems="center" mb={2}>
                  <MDAvatar
                    src={
                      selectedInstruksiAsal.instruksi_asal?.pengirim?.uid_RSS
                        ? `https://smartdh.rotendaokab.go.id/uploads/${selectedInstruksiAsal.instruksi_asal.pengirim.uid_RSS}.jpg`
                        : undefined
                    }
                    size="lg"
                    sx={{ mr: 2 }}
                  />
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="bold">
                      {selectedInstruksiAsal.instruksi_asal?.pengirim?.nama ||
                        "-"}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      Pengirim Instruksi Utama
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="bold"
                  >
                    Tanggal:
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    {selectedInstruksiAsal.instruksi_asal?.tanggal
                      ? formatDate(selectedInstruksiAsal.instruksi_asal.tanggal)
                      : "-"}
                  </MDTypography>
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="bold"
                  >
                    Prioritas:
                  </MDTypography>
                  <Chip
                    label={
                      selectedInstruksiAsal.instruksi_asal?.prioritas || "-"
                    }
                    size="small"
                    sx={{
                      background:
                        PRIORITAS_COLOR[
                          selectedInstruksiAsal.instruksi_asal?.prioritas
                        ] || "#bdbdbd",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="bold"
                    display="block"
                    mb={1}
                  >
                    Isi Instruksi:
                  </MDTypography>
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        selectedInstruksiAsal.instruksi_asal?.isi_instruksi ||
                        "-",
                    }}
                    style={{
                      fontSize: 14,
                      color: "#333",
                      wordBreak: "break-word",
                    }}
                  />
                </MDBox>
                {selectedInstruksiAsal.instruksi_asal?.dokumen && (
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      display="block"
                      mb={1}
                    >
                      Dokumen:
                    </MDTypography>
                    <MDBox
                      display="flex"
                      flexDirection="row"
                      gap={1}
                      flexWrap="wrap"
                    >
                      {(() => {
                        let dokumenArr = [];
                        if (
                          Array.isArray(
                            selectedInstruksiAsal.instruksi_asal.dokumen
                          )
                        ) {
                          dokumenArr =
                            selectedInstruksiAsal.instruksi_asal.dokumen;
                        } else if (
                          typeof selectedInstruksiAsal.instruksi_asal
                            .dokumen === "string" &&
                          selectedInstruksiAsal.instruksi_asal.dokumen
                            .trim()
                            .startsWith("[")
                        ) {
                          try {
                            dokumenArr = JSON.parse(
                              selectedInstruksiAsal.instruksi_asal.dokumen
                            );
                          } catch (e) {
                            dokumenArr = [];
                          }
                        }
                        return dokumenArr.length > 0 ? (
                          dokumenArr.map((link, idx) => {
                            const filename = link.split("/").pop();
                            return (
                              <Button
                                key={idx}
                                href={`${API_ESA_OFFICE_UPLOAD}${link}`}
                                target="_blank"
                                size="small"
                                sx={{
                                  textTransform: "none",
                                  justifyContent: "flex-start",
                                  minWidth: 90,
                                }}
                                variant="outlined"
                                startIcon={getFileIcon(filename)}
                              >
                                {`Dok ${idx + 1}`}
                              </Button>
                            );
                          })
                        ) : (
                          <MDTypography variant="caption" color="text">
                            -
                          </MDTypography>
                        );
                      })()}
                    </MDBox>
                  </MDBox>
                )}
              </Card>
            </MDBox>
          ) : (
            <MDTypography variant="body2" color="text">
              Data tidak tersedia
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenInstruksiAsalDialog(false);
              setSelectedInstruksiAsal(null);
            }}
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Konfirmasi Delete */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Konfirmasi Hapus</DialogTitle>
        <DialogContent>
          <MDTypography>
            Apakah Anda yakin ingin menghapus instruksi yang
            <bb>
              {deleteTarget && deleteTarget.beri_instruksi
                ? " diberikan "
                : " diteruskan "}
            </bb>
            ke
            <b> {deleteTarget?.nama_penerima || "-"} </b>?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="secondary">
            Batal
          </Button>
          <Button onClick={handleDeleteChild} color="error" variant="contained">
            Hapus
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Konfirmasi Delete Instruksi Utama */}
      <Dialog
        open={openDeleteInstruksiDialog}
        onClose={() => setOpenDeleteInstruksiDialog(false)}
      >
        <DialogTitle>Konfirmasi Hapus Instruksi</DialogTitle>
        <DialogContent>
          <MDTypography>
            Apakah Anda yakin ingin menghapus instruksi utama ini beserta
            seluruh penerimanya?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteInstruksiDialog(false)}
            color="secondary"
          >
            Batal
          </Button>
          <Button
            onClick={handleDeleteInstruksi}
            color="error"
            variant="contained"
          >
            Hapus
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Chat - Material Dashboard Style */}
      <Dialog
        open={openChatDialog}
        onClose={handleCloseChat}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: "85vh",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        {/* Header - Material Dashboard Style */}
        <MDBox
          sx={{
            bgcolor: "info.main",
            color: "white",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: 2,
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDAvatar
              src={
                selectedChatPenerima?.uid_RSS
                  ? `https://smartdh.rotendaokab.go.id/uploads/${selectedChatPenerima.uid_RSS}.jpg`
                  : undefined
              }
              alt={selectedChatPenerima?.nama}
              size="sm"
              sx={{ border: "2px solid rgba(255,255,255,0.3)" }}
            />
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" color="white">
                {selectedChatPenerima?.nama || "-"}
              </MDTypography>
              <MDTypography variant="caption" color="white">
                {selectedChatInstruksi?.instruksi_asal?.perihal || "Instruksi"}
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox display="flex" gap={1}>
            <IconButton
              onClick={handleRefreshChat}
              disabled={refreshingChat || loadingChat}
              size="small"
              sx={{
                bgcolor: "white",
                color: refreshingChat ? "grey.500" : "grey.700",
                "&:hover": {
                  bgcolor: "grey.100",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                  color: "grey.500",
                },
              }}
              title="Refresh Chat"
            >
              {refreshingChat ? (
                <CircularProgress size={20} sx={{ color: "grey.500" }} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
            <IconButton
              onClick={handleCloseChat}
              size="small"
              sx={{
                bgcolor: "white",
                color: "grey.700",
                "&:hover": {
                  bgcolor: "grey.100",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </MDBox>

        {/* Chat Messages Area - Material Dashboard Style */}
        <MDBox
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            bgcolor: "grey.100",
          }}
        >
          {loadingChat ? (
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}
            >
              <CircularProgress />
            </MDBox>
          ) : (
            <MDBox
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0,0,0,0.2)",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "rgba(0,0,0,0.3)",
                },
              }}
            >
              {chatMessages.length === 0 ? (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flex={1}
                >
                  <MDTypography variant="body2" color="text.secondary">
                    Belum ada pesan. Mulai percakapan!
                  </MDTypography>
                </MDBox>
              ) : (
                chatMessages.map((message, idx) => {
                  const currentUser =
                    JSON.parse(localStorage.getItem("user")) || {};
                  const currentUserId =
                    userId || currentUser.uid || currentUser.user_id;
                  const isOwnMessage = message.pengirim_id === currentUserId;
                  const isSending = message.isSending;

                  return (
                    <MDBox
                      key={message.id_chat || idx}
                      display="flex"
                      justifyContent={isOwnMessage ? "flex-end" : "flex-start"}
                      sx={{ mb: 0.5, px: 0.5 }}
                    >
                      <MDBox
                        sx={{
                          maxWidth: "75%",
                          minWidth: "100px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: isOwnMessage ? "flex-end" : "flex-start",
                        }}
                      >
                        {/* Avatar dan nama untuk pesan lawan */}
                        {!isOwnMessage && (
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            mb={0.3}
                            sx={{ px: 0.5 }}
                          >
                            <MDAvatar
                              src={
                                message.pengirim_profil
                                  ? `https://smartdh.rotendaokab.go.id/uploads/${message.pengirim_profil}.jpg`
                                  : undefined
                              }
                              size="xs"
                            />
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                            >
                              {message.pengirim_nama || "-"}
                            </MDTypography>
                          </MDBox>
                        )}

                        {/* Bubble Chat */}
                        <MDBox
                          sx={{
                            p: 1.25,
                            px: 1.5,
                            borderRadius: isOwnMessage
                              ? "7.5px 0 7.5px 7.5px"
                              : "0 7.5px 7.5px 7.5px",
                            backgroundColor: isOwnMessage ? "#4caf50" : "white",
                            color: isOwnMessage ? "white" : "text.primary",
                            wordBreak: "break-word",
                            boxShadow: 1,
                            position: "relative",
                            opacity: isSending ? 0.7 : 1,
                            border: isOwnMessage ? "none" : "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <MDTypography
                            variant="body2"
                            sx={{
                              color: isOwnMessage ? "white" : "text.primary",
                              fontSize: "0.95rem",
                              lineHeight: 1.4,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {message.pesan}
                            {isSending && (
                              <MDBox
                                component="span"
                                sx={{
                                  display: "inline-block",
                                  ml: 0.5,
                                  verticalAlign: "middle",
                                }}
                              >
                                <CircularProgress
                                  size={12}
                                  sx={{ color: "white" }}
                                />
                              </MDBox>
                            )}
                          </MDTypography>
                        </MDBox>

                        {/* Timestamp dan Read Status */}
                        <MDBox
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          sx={{ mt: 0.2, px: 0.5 }}
                        >
                          <MDTypography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              color: "#4caf50",
                              fontWeight: 400,
                            }}
                          >
                            {formatChatTime(message.waktu_kirim)}{" "}
                          </MDTypography>
                          {isOwnMessage && !isSending && (
                            <MDBox
                              sx={{
                                width: "12px",
                                height: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CheckCircleIcon
                                sx={{
                                  fontSize: "12px",
                                  color: message.waktu_baca
                                    ? "rgba(11, 183, 25, 0.7)"
                                    : "rgba(255,255,255,0.6)",
                                }}
                              />
                            </MDBox>
                          )}
                          {isOwnMessage && message.waktu_baca && (
                            <MDTypography
                              variant="caption"
                              sx={{
                                fontSize: "0.6rem",
                                color: "rgba(11, 183, 25, 0.7)",
                                fontStyle: "italic",
                              }}
                            >
                              Dibaca {formatChatTime(message.waktu_baca)}
                            </MDTypography>
                          )}
                          {/* Tombol Delete */}
                          {isOwnMessage && !isSending && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteMessage(message.id_chat)
                              }
                              sx={{
                                width: "20px",
                                height: "20px",
                                color: "rgba(251, 27, 15, 0.7)",
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: "14px" }} />
                            </IconButton>
                          )}
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </MDBox>
          )}
        </MDBox>

        {/* Input Area - Material Dashboard Style */}
        <MDBox
          sx={{
            bgcolor: "grey.50",
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            placeholder="Ketik pesan..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            multiline
            maxRows={4}
            size="small"
            disabled={loadingChat}
            sx={{
              bgcolor: "white",
              borderRadius: "20px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                "& fieldset": {
                  borderColor: "rgba(0,0,0,0.1)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0,0,0,0.2)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000",
                },
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || loadingChat}
            sx={{
              bgcolor: "#000000",
              color: "white",
              width: "40px",
              height: "40px",
              "&:hover": {
                bgcolor: "#333333",
              },
              "&:disabled": {
                bgcolor: "rgba(0,0,0,0.1)",
                color: "rgba(0,0,0,0.3)",
              },
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </MDBox>
      </Dialog>
    </DashboardLayout>
  );
}

export default DaftarPenugasan;
